import { compact, debounce, get, identity, isEmpty, isEqual, noop, uniq } from "lodash";
import { action, comparer, makeAutoObservable, reaction, runInAction, toJS } from "mobx";
import { AxiosInstance, AxiosResponse } from "axios";
import { ChangeOfAuditor, ChangeOfAuditorForm } from "../auditor/auditorDomain";
import {
  failure,
  GetProjectDocumentSignersResponse,
  isOk,
  LocalizedTextForDocument,
  ok,
  pending,
  Result,
  SubscriptionTypeModel,
} from "../../common/common";
import { History } from "history";
import {
  AddDecisionsEvent,
  AddDocumentsEvent,
  anyDocumentSentForSigning,
  applyCompanyRoleEmails,
  applyDecisionFailureEvent,
  applyDecisionSuccessEvent,
  applyShareholderEmail,
  BatchDocumentOverrrides,
  categoryMapping,
  categorySubscriptionMapping,
  CleanupFunction,
  CompanyRoleIdWithName,
  ConfirmRemoveDocumentEvent,
  CopyDecisionsEvent,
  DbotEvent,
  DbotEventDispatcher,
  DecisionContext,
  DecisionItem,
  DecisionStatusResponse,
  DecisionTypeHandler,
  defaultDocumentLanguage,
  DetailsPanelRef,
  DocumentContext,
  DocumentItem,
  DocumentPreview,
  DocumentTypeHandler,
  DocumentViewingStatus,
  fetchDecisionStatusEvent,
  fetchDecisionStatusResultEvent,
  fetchIncreaseOfShareCapitalSubscribersAllocationFailureEvent,
  fetchIncreaseOfShareCapitalSubscribersAllocationSuccessEvent,
  fetchVotingResultEvent,
  findStateForDecision,
  Freetext,
  GeneralMeetingParticipation,
  getDecisionActionLabel,
  GetDocumentViewingStatusResponse,
  getProjectCompanies,
  GetProjectResponse,
  getSigningStatusEvent,
  getSigningStatusFailureEvent,
  getSigningStatusSuccessEvent,
  getSubscriptionStatusFailureEvent,
  getSubscriptionStatusSuccessEvent,
  getViewingStatusFailureEvent,
  getViewingStatusSuccessEvent,
  InputState,
  isDbotEvent,
  isDocumentLocked,
  isDocumentSent,
  isDocumentSentForReview,
  isDocumentSentForSigning,
  isDocumentSentForVoting,
  isDocumentSigned,
  isRemoveDecision,
  isRemovingDecision,
  mapDocumentLanguageToLocales,
  mapToCreateProjectRequest,
  mapToSendForSigningRequest,
  PowerOfAttorneyForGeneralMeeting,
  ProjectCompanyContext,
  ProjectContext,
  ProjectControllerState,
  ProjectDocumentStatus,
  ProjectInitializationParams,
  ProjectJsonData,
  ProjectsForCopyResponse,
  ProjectStatus,
  readyState,
  Recipient,
  RecipientId,
  refreshProjectStatusEvent,
  refreshProjectStatusFailureEvent,
  refreshProjectStatusSuccessEvent,
  registerNewSignerFailureEvent,
  registerNewSignerSuccessEvent,
  removeDecisionState,
  removingDecisionState,
  reopenDocumentResultEvent,
  resendNoticeOfBoardMeetingFailureEvent,
  resendNoticeOfBoardMeetingRequest,
  resendNoticeOfBoardMeetingSuccessEvent,
  saveGeneralMeetingVotesErrorEvent,
  saveGeneralMeetingVotesSuccessEvent,
  SaveProjectEvent,
  saveShareAllocationFailureEvent,
  saveShareAllocationSuccessEvent,
  searchCompaniesResultEvent,
  sendDocumentForSigningResultEvent,
  sendDocumentForViewingFailureEvent,
  sendDocumentForViewingSuccessEvent,
  sendDocumentsForViewingRequest,
  sendSubscriptionFormForSigningResult,
  sendToBRFailureEvent,
  sendToBRSuccessEvent,
  ShareAllocationRoundsResponse,
  sortDocuments,
  sortedCompanies,
  TabId,
  UpdateBoardMeetingRecipientItem,
  updateBoardMeetingRecipientsFailureEvent,
  updateBoardMeetingRecipientsSuccessEvent,
  updateCompanyRoleEmailsFailureEvent,
  updateCompanyRoleEmailsSuccessEvent,
  UpdateCompanyRoleItem,
  updateFollowUpStatusResultEvent,
  updateInputStateEvent,
} from "../common/commonDomain";
import {
  CompanyNameChange,
  CompanyNameChangeForm,
} from "../companyNameChange/companyNameChangeDomain";
import {
  ApproveAnnualAccount,
  ApproveAnnualAccountForm,
} from "../approveAnnualAccount/approveAnnualAccountDomain";
import {
  ApproveCompanyBoardRemuneration,
  ApproveCompanyBoardRemunerationForm,
} from "../approveCompanyBoardRemuneration/approveCompanyBoardRemunerationDomain";
import {
  ApproveNominationCommitteeRemuneration,
  ApproveNominationCommitteeRemunerationForm,
} from "../approveNominationCommitteeRemuneration/approveNominationCommitteeRemunerationDomain";
import {
  ElectNominationCommittee,
  ElectNominationCommitteeForm,
} from "../electNominationCommittee/electNominationCommitteeDomain";
import {
  ApproveAuditorRemuneration,
  ApproveAuditorRemunerationForm,
} from "../approveAuditorRemuneration/approveAuditorRemunerationDomain";
import {
  BoardAssignsSignature,
  BoardAssignsSignatureForm,
} from "../boardAssignsSignature/boardAssignsSignatureDomain";
import {
  EmploymentOfManagingDirector,
  EmploymentOfManagingDirectorForm,
} from "../employmentOfManagingDirector/employmentOfManagingDirectorDomain";
import {
  IncreaseOfShareCapital,
  IncreaseOfShareCapitalForm,
} from "../increaseOfShareCapital/increaseOfShareCapitalDomain";
import {
  RemoveSignatureFromArticlesOfAssociation,
  RemoveSignatureFromArticlesOfAssociationForm,
} from "../removeSignatureFromArticlesOfAssociation/removeSignatureFromArticlesOfAssociationDomain";
import { Procura, ProcuraForm } from "../procura/procuraDomain";
import {
  InsertSignatureInArticlesOfAssociation,
  InsertSignatureInArticlesOfAssociationForm,
} from "../insertSignatureInArticlesOfAssociation/insertSignatureInArticlesOfAssociationDomain";
import {
  ChangeSignatureInArticlesOfAssociation,
  ChangeSignatureInArticlesOfAssociationForm,
} from "../changeSignatureInArticlesOfAssociation/changeSignatureInArticlesOfAssociationDomain";
import {
  RemoveCompanyBoardFromArticlesOfAssociation,
  RemoveCompanyBoardFromArticlesOfAssociationForm,
} from "../removeCompanyBoardFromArticlesOfAssociation/removeCompanyBoardFromArticlesOfAssociationDomain";
import {
  InsertCompanyBoardInArticlesOfAssociation,
  InsertCompanyBoardInArticlesOfAssociationForm,
} from "../insertCompanyBoardInArticlesOfAssociation/insertCompanyBoardInArticlesOfAssociationDomain";
import {
  NoticeOfBoardMeeting,
  NoticeOfBoardMeetingForm,
} from "../noticeBoardMeeting/noticeBoardMeetingDomain";
import { NoticeToGeneralMeetingForm } from "../noticeGeneralMeeting/noticeGeneralMeetingDomain";
import { GeneralMeetingMinutesForm } from "../generalMeetingMinutes/generalMeetingMinutesDomain";
import { BoardMeetingMinutes } from "../boardMeetingMinutes/boardMeetingMinutesDomain";
import { SubscriptionForm, SubscriptionFormForm } from "../subscriptionForm/subscriptionFormDomain";
import { FreetextForm, showFreetext } from "../freetext/freetextDomain";
import {
  AssignsNewCompanyBoard,
  AssignsNewCompanyBoardForm,
} from "../assignsNewCompanyBoard/assignsNewCompanyBoardDomain";
import { approveAnnualAccountHandler } from "../approveAnnualAccount/approveAnnualAccountHandler";
import { approveAuditorRemunerationHandler } from "../approveAuditorRemuneration/approveAuditorRemunerationHandler";
import { approveCompanyBoardRemunerationHandler } from "../approveCompanyBoardRemuneration/approveCompanyBoardRemunerationHandler";
import { approveNominationCommitteeRemunerationHandler } from "../approveNominationCommitteeRemuneration/approveNominationCommitteeRemunerationHandler";
import { assignsNewCompanyBoardHandler } from "../assignsNewCompanyBoard/assignsNewCompanyBoardHandler";
import { auditorHandler } from "../auditor/auditorHandler";
import { boardAssignsSignatureHandler } from "../boardAssignsSignature/boardAssignsSignatureHandler";
import { changeSignatureInArticlesOfAssociationHandler } from "../changeSignatureInArticlesOfAssociation/changeSignatureInArticlesOfAssociationHandler";
import { companyNameHandler } from "../companyNameChange/companyNameHandler";
import { electNominationCommitteeHandler } from "../electNominationCommittee/electNominationCommitteeHandler";
import { employmentOfManagingDirectorHandler } from "../employmentOfManagingDirector/employmentOfManagingDirectorHandler";
import { freetextHandler } from "../freetext/freetextHandler";
import { increaseOfShareCapitalHandler } from "../increaseOfShareCapital/increaseOfShareCapitalHandler";
import { insertCompanyBoardInArticlesOfAssociationHandler } from "../insertCompanyBoardInArticlesOfAssociation/insertCompanyBoardInArticlesOfAssociationHandler";
import { insertSignatureInArticlesOfAssociationHandler } from "../insertSignatureInArticlesOfAssociation/insertSignatureInArticlesOfAssociationHandler";
import { procuraHandler } from "../procura/procuraHandler";
import { removeCompanyBoardFromArticlesOfAssociationHandler } from "../removeCompanyBoardFromArticlesOfAssociation/removeCompanyBoardFromArticlesOfAssociationHandler";
import { removeSignatureFromArticlesOfAssociationHandler } from "../removeSignatureFromArticlesOfAssociation/removeSignatureFromArticlesOfAssociationHandler";
import {
  createGeneralMeetingFields,
  GeneralMeetingFields,
  GeneralMeetingMinutes,
  GeneralMeetingType,
  GeneralMeetingVotingType,
  NoticeToGeneralMeeting,
  prepareGeneralMeetingForSave,
} from "../common/commonGeneralMeetingDomain";
import { CompanyInfo, CompanyRole, SuggestedCompany } from "../../types/CompanyInfo";
import { SummaryController } from "../summary/SummaryController";
import {
  CapitalIncreaseThroughBonusIssue,
  CapitalIncreaseThroughBonusIssueForm,
} from "../capitalIncreaseThroughBonusIssue/capitalIncreaseThroughBonusIssueDomain";
import { capitalIncreaseThroughBonusIssueHandler } from "../capitalIncreaseThroughBonusIssue/capitalIncreaseThroughBonusIssueHandler";
import {
  ArticlesOfAssociation,
  ArticlesOfAssociationForm,
} from "../articlesofassociation/articlesofassociationDomain";
import { logDebug } from "../../utils/log";
import { ConflictMessage } from "./projectComponents";
import { SplitShare, SplitShareForm } from "../splitShare/splitShareDomain";
import { splitShareHandler } from "../splitShare/splitShareHandler";
import {
  ReverseSplitShare,
  ReverseSplitShareForm,
} from "../reverseSplitShare/reverseSplitShareDomain";
import { reverseSplitHandler } from "../reverseSplitShare/reverseSplitShareHandler";
import { boardMeetingMinutesHandler } from "../boardMeetingMinutes/boardMeetingMinutesHandler";
import { BoardMeetingMinutesForm } from "../boardMeetingMinutes/BoardMeetingMinutesForm";
import {
  ChangeCompanyBoardInArticlesOfAssociation,
  ChangeCompanyBoardInArticlesOfAssociationForm,
} from "../changeCompanyBoardInArticlesOfAssociation/changeCompanyBoardInArticlesOfAssociationDomain";
import { changeCompanyBoardInArticlesOfAssociationHandler } from "../changeCompanyBoardInArticlesOfAssociation/changeCompanyBoardInArticlesOfAssociationHandler";
import { convertibleLoanPoAHandler } from "../convertibleLoanPoA/convertibleLoanPoAHandler";
import {
  ConvertibleLoanPowerOfAttorney,
  ConvertibleLoanPowerOfAttorneyForm,
} from "../convertibleLoanPoA/convertibleLoanPoADomain";
import {
  GroupContributionByGeneralMeeting,
  GroupContributionByGeneralMeetingForm,
} from "../groupContributionByGeneralMeeting/groupContributionByGeneralMeetingDomain";
import { groupContributionByGeneralMeetingHandler } from "../groupContributionByGeneralMeeting/groupContributionByGeneralMeetingHandler";
import { dividendHandler } from "../dividend/dividendHandler";
import { dividendPowerOfAttorneyHandler } from "../dividendPowerOfAttorney/dividendPowerOfAttorneyHandler";
import { move, next, prev } from "../../utils/arrays";
import {
  IncreaseShareCapitalPowerOfAttorney,
  IncreaseShareCapitalPowerOfAttorneyForm,
} from "../increaseShareCapitalPowerOfAttorney/increaseShareCapitalPowerOfAttorneyDomain";
import { increaseShareCapitalPowerOfAttorneyHandler } from "../increaseShareCapitalPowerOfAttorney/increaseShareCapitalPowerOfAttorneyHandler";
import {
  BoardMeetingFields,
  createBoardMeetingFields,
  prepareBoardMeetingForSave,
  ProjectCompanyRole,
  toRecipients,
} from "../common/commonBoardMeetingDomain";
import { DecisionListItem, getDecisionFollowUpPanels } from "../summary/summaryDomain";
import {
  DocumentApiClient,
  DocumentSigningStatus,
  GetDocumentSigningStatusResponse,
  SigningStatusForSigner,
} from "../../document/documentApi";
import {
  FileUploadConfig,
  ProjectDecisionAttachment,
  UploadedAttachment,
} from "../../fileUpload/fileUploadDomain";
import { deleteAttachment, downloadAttachment, uploadAttachments } from "../attachments";
import { downloadDocument } from "../../api/DownloadService";
import { Dividend, DividendForm } from "../dividend/dividendDomain";
import { AccessType } from "../../types/enums/AccessType";
import {
  ReductionOfShareCapital,
  ReductionOfShareCapitalForm,
} from "../reductionOfShareCapital/reductionOfShareCapitalDomain";
import { reductionOfShareCapitalHandler } from "../reductionOfShareCapital/reductionOfShareCapitalHandler";
import { User } from "../../types/UserInfo";
import {
  navigateToProject,
  navigateToProjectList,
  openLinkInNewWindow,
} from "../../utils/navigation";
import { DropdownMenuItems } from "../../components-v2/Dropdown";
import { AIClient } from "../../ai/AIAssistant";
import { downloadFile, getFilenameFromHeaders, HttpStatus, isHttpError } from "../../api";
import { noAuditorHandler } from "../noAuditor/noAuditorHandler";
import { NoAuditor, NoAuditorForm } from "../noAuditor/noAuditorDomain";
import { noticeBoardMeetingHandler } from "../noticeBoardMeeting/noticeBoardMeetingHandler";
import { noticeGeneralMeetingHandler } from "../noticeGeneralMeeting/noticeGeneralMeetingHandler";
import { generalMeetingMinutesHandler } from "../generalMeetingMinutes/generalMeetingMinutesHandler";
import { subscriptionFormHandler } from "../subscriptionForm/subscriptionFormHandler";
import { articlesOfAssociationHandler } from "../articlesofassociation/articlesOfAssociationHandler";
import { TagProps } from "../../components-v2/Tag";
import {
  DividendPowerOfAttorney,
  DividendPowerOfAttorneyForm,
} from "../dividendPowerOfAttorney/dividendPowerOfAttorneyDomain";
import { ReactElement } from "react";
import ReviewMinutesAdminInitializer from "../../meetings/ReviewMinutesPageAdmin";
import { MeetingEmailInitializerController } from "../../noticeForBoardMember/noticeForBoardMemberDomain";
import { BoardMeetingVote } from "../boardMeetingMinutes/boardMeetingVoting";
import { AdminElectronicGeneralMeetingController } from "../../meetings/generalMeeting/electronicGeneralMeetingDomain";
import AdminElectronicGeneralMeetingInitializer from "../../meetings/generalMeeting/AdminElectronicGeneralMeetingPage";
import { Language } from "../../i18n/languages";
import {
  ChangeArticlesOfAssociation,
  ChangeArticlesOfAssociationForm,
} from "../changeArticlesOfAssociation/changeArticlesOfAssociationDomain";
import { changeArticlesOfAssociationHandler } from "../changeArticlesOfAssociation/changeArticlesOfAssociationHandler";
import { TFunction, TMessage } from "../../i18n/tt";
import {
  DocumentSelection,
  MeetingForm,
  MeetingOption,
  MeetingType,
} from "../../meetings/meetingsDomain";
import {
  GetShareholdersResponse,
  prepShareholderData,
  ShareholderData,
  ShareholderField,
} from "../../types/Shareholder";
import { DecisionType } from "../../common/decisions";
import { DocumentType, getDocumentLabel } from "../../common/documents";
import { deferred, when } from "../../utils/functions";
import {
  ConversionOfShares,
  ConversionOfSharesForm,
} from "../conversionOfShares/conversionOfSharesDomain";
import { conversionOfSharesHandler } from "../conversionOfShares/conversionOfSharesHandler";
import { toProjectReference } from "../../utils/UrnReference";
import { ObjectPermission } from "../../types/Permissions";
import {
  confirmAction,
  DCEvent,
  showError,
  showGeneralError,
  showPaymentDialog,
  showSuccess,
  showWarning,
  thunk,
} from "../../events/eventsDomain";
import { StringFieldModel } from "../../common/commonForms";
import { PaidSubscription, SubscriptionType } from "../../common/subscription";
import { customDocumentHandler } from "../customDocument/customDocumentHandler";
import {
  CustomDocumentData,
  CustomDocumentForm,
  CustomDocumentOverrides,
  CustomDocumentType,
} from "../customDocument/customDocumentDomain";
import { isProductionEnvironment } from "../../utils/environments";
import { featureTogglesForCompany } from "../../common/featureToggles";
import { acquireOwnSharesPowerOfAttorneyHandler } from "../acquireOwnSharesPowerOfAttorney/acquireOwnSharesPowerOfAttorneyHandler";
import {
  AcquireOwnSharesPowerOfAttorney,
  AcquireOwnSharesPowerOfAttorneyForm,
} from "../acquireOwnSharesPowerOfAttorney/acquireOwnSharesPowerOfAttorneyDomain";
import { SelectProps } from "../../components-v2/Select";
import { DownloadAsPdfButtonProps } from "../../common/commonComponents";
import { requiresSubscription } from "../../payment/Payment";
import { toQueryString } from "../../utils/queryParams";
import { DataRequest } from "../../ai/autotext";
import { electionOfCompanyBoardHandler } from "../ElectionOfCompanyBoard/electionOfCompanyBoardHandler";
import {
  ElectionOfCompanyBoard,
  ElectionOfCompanyBoardForm,
} from "../ElectionOfCompanyBoard/electionOfCompanyBoardDomain";

///
/// API
///

export type CreateMeetingRequest = {
  orgNo: string;
  projectName: string;
  meetingType: MeetingType;
  meetingDocumentSelection?: DocumentSelection;
  meetingForm?: MeetingForm;
  meetingStartDate: string;
  meetingEndDate?: string;
  conductElectronicGeneralMeeting?: boolean;
  meetingOptions?: MeetingOption[];
  meetingDocumentLanguage?: Language[];
  duplicateProjectId?: string;
  ordinaryGeneralMeeting?: boolean;
  isGeneratedName?: boolean;
};

export type ProjectApi = {
  downloadAttachmentUrl: (params: { projectId: string; attachmentId: string }) => string;
};

export const projectApi: ProjectApi = {
  downloadAttachmentUrl: ({ projectId, attachmentId }) =>
    `/api/project/${projectId}/attachment/${attachmentId}`,
};

///
/// Navigation state
///

type ProjectViewState = {
  activeTab?: TabId;
  detailsPanel?: DetailsPanelRef;
  detailsPanelMaximized?: boolean;
  displayMode?: "normal" | "fullscreen" | "screen-share" | "overlay";
  isMobileDocumentPreviewOpen?: boolean;
  selectedDocumentId?: string;
  selectedDocumentPreview?: string;
};

export type ProjectNavigationState = {
  type: "project-navigation-state";
  from?: "create-new" | string;
  overlay?: boolean;
  documentId?: string;
  view?: ProjectViewState;
};

export const isProjectNavigationState = (x: unknown): x is ProjectNavigationState =>
  get(x, "type") === "project-navigation-state";

export const asProjectNavigationState = (x: unknown): ProjectNavigationState | undefined =>
  isProjectNavigationState(x) ? x : undefined;

const toProjectNavigationState = (
  from?: string,
  view?: ProjectViewState,
): ProjectNavigationState => ({ type: "project-navigation-state", from, view });

//
// Decision
//

export type DecisionData =
  | ApproveAnnualAccount
  | ApproveAuditorRemuneration
  | ApproveCompanyBoardRemuneration
  | ApproveNominationCommitteeRemuneration
  | AssignsNewCompanyBoard
  | BoardAssignsSignature
  | CapitalIncreaseThroughBonusIssue
  | ChangeCompanyBoardInArticlesOfAssociation
  | ChangeOfAuditor
  | ChangeSignatureInArticlesOfAssociation
  | CompanyNameChange
  | ConvertibleLoanPowerOfAttorney
  | Dividend
  | DividendPowerOfAttorney
  | ElectNominationCommittee
  | EmploymentOfManagingDirector
  | Freetext
  | GroupContributionByGeneralMeeting
  | IncreaseOfShareCapital
  | IncreaseShareCapitalPowerOfAttorney
  | InsertCompanyBoardInArticlesOfAssociation
  | InsertSignatureInArticlesOfAssociation
  | NoAuditor
  | Procura
  | ReductionOfShareCapital
  | RemoveCompanyBoardFromArticlesOfAssociation
  | RemoveSignatureFromArticlesOfAssociation
  | ReverseSplitShare
  | SplitShare
  | ChangeArticlesOfAssociation
  | ConversionOfShares
  | AcquireOwnSharesPowerOfAttorney
  | ElectionOfCompanyBoard;

export type DecisionForm =
  | ApproveAnnualAccountForm
  | ApproveAuditorRemunerationForm
  | ApproveCompanyBoardRemunerationForm
  | ApproveNominationCommitteeRemunerationForm
  | AssignsNewCompanyBoardForm
  | BoardAssignsSignatureForm
  | CapitalIncreaseThroughBonusIssueForm
  | ChangeCompanyBoardInArticlesOfAssociationForm
  | ChangeOfAuditorForm
  | ChangeSignatureInArticlesOfAssociationForm
  | CompanyNameChangeForm
  | ConvertibleLoanPowerOfAttorneyForm
  | DividendForm
  | DividendPowerOfAttorneyForm
  | ElectNominationCommitteeForm
  | EmploymentOfManagingDirectorForm
  | FreetextForm
  | GroupContributionByGeneralMeetingForm
  | IncreaseOfShareCapitalForm
  | IncreaseShareCapitalPowerOfAttorneyForm
  | InsertCompanyBoardInArticlesOfAssociationForm
  | InsertSignatureInArticlesOfAssociationForm
  | NoAuditorForm
  | ProcuraForm
  | ReductionOfShareCapitalForm
  | RemoveCompanyBoardFromArticlesOfAssociationForm
  | RemoveSignatureFromArticlesOfAssociationForm
  | ReverseSplitShareForm
  | SplitShareForm
  | ChangeArticlesOfAssociationForm
  | ConversionOfSharesForm
  | AcquireOwnSharesPowerOfAttorneyForm
  | ElectionOfCompanyBoardForm;

export type Decision = {
  decisionId: string;
  decisionData: DecisionData;
  decisionTexts: LocalizedTextForDocument[];
  attachments: ProjectDecisionAttachment[];
};

export type DecisionAndForm<T extends DecisionType> = {
  type: T;
  decisionId: string;
  decision: Decision;
  form: DecisionForm;
  decisionTexts: LocalizedTextForDocument[];
  attachments: ProjectDecisionAttachment[];
};

export const handlers = {
  [DecisionType.approveAnnualAccount]: approveAnnualAccountHandler,
  [DecisionType.approveAuditorRemuneration]: approveAuditorRemunerationHandler,
  [DecisionType.approveCompanyBoardRemuneration]: approveCompanyBoardRemunerationHandler,
  [DecisionType.approveNominationCommitteeRemuneration]:
    approveNominationCommitteeRemunerationHandler,
  [DecisionType.assignsNewCompanyBoard]: assignsNewCompanyBoardHandler,
  [DecisionType.auditor]: auditorHandler,
  [DecisionType.boardAssignsSignature]: boardAssignsSignatureHandler,
  [DecisionType.capitalIncreaseThroughBonusIssue]: capitalIncreaseThroughBonusIssueHandler,
  [DecisionType.changeCompanyBoardInArticlesOfAssociation]:
    changeCompanyBoardInArticlesOfAssociationHandler,
  [DecisionType.changeSignatureInArticlesOfAssociation]:
    changeSignatureInArticlesOfAssociationHandler,
  [DecisionType.companyName]: companyNameHandler,
  [DecisionType.convertibleLoanPowerOfAttorney]: convertibleLoanPoAHandler,
  [DecisionType.dividend]: dividendHandler,
  [DecisionType.dividendPowerOfAttorney]: dividendPowerOfAttorneyHandler,
  [DecisionType.electNominationCommittee]: electNominationCommitteeHandler,
  [DecisionType.employmentOfManagingDirector]: employmentOfManagingDirectorHandler,
  [DecisionType.freetext]: freetextHandler,
  [DecisionType.groupContributionByGeneralMeeting]: groupContributionByGeneralMeetingHandler,
  [DecisionType.increaseOfShareCapital]: increaseOfShareCapitalHandler,
  [DecisionType.increaseShareCapitalPowerOfAttorney]: increaseShareCapitalPowerOfAttorneyHandler,
  [DecisionType.insertCompanyBoardInArticlesOfAssociation]:
    insertCompanyBoardInArticlesOfAssociationHandler,
  [DecisionType.insertSignatureInArticlesOfAssociation]:
    insertSignatureInArticlesOfAssociationHandler,
  [DecisionType.noAuditor]: noAuditorHandler,
  [DecisionType.procura]: procuraHandler,
  [DecisionType.reductionOfShareCapital]: reductionOfShareCapitalHandler,
  [DecisionType.removeCompanyBoardFromArticlesOfAssociation]:
    removeCompanyBoardFromArticlesOfAssociationHandler,
  [DecisionType.removeSignatureFromArticlesOfAssociation]:
    removeSignatureFromArticlesOfAssociationHandler,
  [DecisionType.reverseSplitShare]: reverseSplitHandler,
  [DecisionType.splitShare]: splitShareHandler,
  [DecisionType.changeArticlesOfAssociation]: changeArticlesOfAssociationHandler,
  [DecisionType.conversionOfShares]: conversionOfSharesHandler,
  [DecisionType.acquireOwnSharesPowerOfAttorney]: acquireOwnSharesPowerOfAttorneyHandler,
  [DecisionType.electionOfCompanyBoard]: electionOfCompanyBoardHandler,
};

export function getHandler(
  type: DecisionType,
): DecisionTypeHandler<DecisionType, DecisionData, DecisionForm> {
  if (!(type in handlers)) {
    throw new Error(`Missing handler for type: ${type}`);
  }
  return handlers[type] as DecisionTypeHandler<DecisionType, DecisionData, DecisionForm>;
}

export function applyForm<T extends DecisionType>({
  type,
  decisionId,
  form,
  decisionTexts,
  attachments,
}: DecisionAndForm<T>): Decision {
  return {
    decisionId,
    decisionTexts,
    attachments: applyDecisionAttachments(attachments),
    decisionData: getHandler(type).applyForm(form),
  };
}

function applyDecisionAttachments(
  attachments: ProjectDecisionAttachment[],
): ProjectDecisionAttachment[] {
  return attachments.map((a) => ({
    id: a.id,
    filename: a.filename,
    selectedDocuments: a.selectedDocuments.slice(),
  }));
}

export function prepareDecisionsForSave(decisions?: DecisionAndForm<DecisionType>[]): Decision[] {
  return decisions?.map((decisionAndForm) => applyForm(decisionAndForm)) ?? [];
}

export function removeDecision(
  decisions: DecisionAndForm<DecisionType>[],
  type: DecisionType,
): DecisionAndForm<DecisionType> | undefined {
  const index = decisions.findIndex((x) => x.type === type);
  if (index !== -1) {
    return decisions.splice(index, 1)[0];
  }
}

export function removeDecisionById(
  decisions: DecisionAndForm<DecisionType>[],
  id: string,
): DecisionAndForm<DecisionType> | undefined {
  const index = decisions.findIndex((x) => x.decisionId === id);
  if (index !== -1) {
    return decisions.splice(index, 1)[0];
  }
}

//
// Document
//

export type DocumentData =
  | ArticlesOfAssociation
  | NoticeOfBoardMeeting
  | BoardMeetingMinutes
  | NoticeToGeneralMeeting
  | GeneralMeetingMinutes
  | SubscriptionForm
  | CustomDocumentData;

export type DocumentOverrides = CustomDocumentOverrides;

export type DocumentForm =
  | NoticeOfBoardMeetingForm
  | NoticeToGeneralMeetingForm
  | BoardMeetingMinutesForm
  | GeneralMeetingMinutesForm
  | SubscriptionFormForm
  | ArticlesOfAssociationForm
  | CustomDocumentForm;

export type Document = {
  documentId: string;
  documentData: DocumentData;
  attachments?: UploadedAttachment[];
};

export type DocumentAndForm = {
  documentId: string;
  type: DocumentType;
  form: DocumentForm;
  attachments: UploadedAttachment[];
};

export const documentsHandlers = {
  [DocumentType.notice_board_meeting]: noticeBoardMeetingHandler,
  [DocumentType.notice_general_meeting]: noticeGeneralMeetingHandler,
  [DocumentType.board_meeting]: boardMeetingMinutesHandler,
  [DocumentType.general_meeting]: generalMeetingMinutesHandler,
  [DocumentType.subscription_form]: subscriptionFormHandler,
  [DocumentType.articlesofassociation]: articlesOfAssociationHandler,
  [DocumentType.custom_document]: customDocumentHandler,
};

export function getDocumentHandler(
  type: DocumentType,
): DocumentTypeHandler<DocumentType, DocumentData, DocumentForm> {
  if (!(type in documentsHandlers)) {
    throw new Error(`Missing handler for type: ${type}`);
  }
  return documentsHandlers[type] as DocumentTypeHandler<DocumentType, DocumentData, DocumentForm>;
}

export function applyDocumentForm({
  documentId,
  type,
  form,
  attachments,
}: DocumentAndForm): Document {
  return {
    documentId: documentId,
    documentData: getDocumentHandler(type).applyForm(form),
    attachments: attachments.slice(),
  };
}

export function prepareDocumentsForSave(documents?: DocumentAndForm[]): Document[] {
  return documents?.map(applyDocumentForm) ?? [];
}

export function applyDocumentOverrides(
  { type, documentId, form }: DocumentAndForm,
  orgNos: string[],
): BatchDocumentOverrrides[] {
  return compact(
    orgNos.map((orgNo) => {
      const overrides = getDocumentHandler(type).applyOverrides?.(form, orgNo);
      return overrides ? { documentId, orgNo, overrides } : undefined;
    }),
  );
}

export function prepareBatchDocumentOverrides(
  documents: DocumentAndForm[],
  orgNos: string[],
): BatchDocumentOverrrides[] {
  return documents
    .flatMap((d) => applyDocumentOverrides(d, orgNos))
    .filter((x) => x.overrides !== undefined);
}

export function removeDocument(
  documents: DocumentAndForm[],
  documentId: string,
): DocumentAndForm | undefined {
  const index = documents.findIndex((x) => x.documentId === documentId);
  if (index !== -1) {
    return documents.splice(index, 1)[0];
  }
}

export function getDocumentLanguages(documents: Document[], { orgNo }: { orgNo: string }) {
  const languages = uniq(documents.flatMap((d) => d.documentData.documentLanguage ?? []));
  return languages.length > 0 ? languages : defaultDocumentLanguage(orgNo);
}

export function findNoticeToBoardMeetingForm(
  documents: DocumentAndForm[],
): NoticeOfBoardMeetingForm | undefined {
  return documents
    .map((d) => d.form)
    .flatMap((f) => (f.type === DocumentType.notice_board_meeting ? [f] : []))[0];
}

export function findBoardMinutesForm(
  documents: DocumentAndForm[],
): BoardMeetingMinutesForm | undefined {
  return documents
    .map((d) => d.form)
    .flatMap((f) => (f.type === DocumentType.board_meeting ? [f] : []))[0];
}

export function findNoticeToGeneralMeetingForm(
  documents: DocumentAndForm[],
): NoticeToGeneralMeetingForm | undefined {
  return documents.map((d) => d.form).find((f) => f.type === DocumentType.notice_general_meeting);
}

export function findDateNoticeGeneralMeeting(documents: DocumentAndForm[]): string | undefined {
  return findNoticeToGeneralMeetingForm(documents)?.dateNoticeGeneralMeeting?.validatedValue;
}

///
/// Project controller
///

class ContextWrapper implements ProjectContext {
  constructor(
    private projectController: ProjectController,
    private projectParams: ProjectInitializationParams,
  ) {
    makeAutoObservable(this);
  }

  get userPrefs() {
    return this.projectParams.rootStoreData.userPrefs ?? {};
  }

  get companyInfo(): CompanyInfo {
    return this.projectParams.companyInfo;
  }

  get shareholderData(): ShareholderData {
    return this.projectParams.shareholderData;
  }

  get currentDecisions(): DecisionItem[] {
    return this.projectController.decisions.map((d) =>
      toDecisionItem(
        d,
        this.projectController.invalidDecisions.has(d.decisionId),
        this.projectController.project.decisionsRequiresGeneralMeeting.includes(d.decisionId),
        this.projectController.project.defaultDecisionsGeneralMeetingVotingType[d.decisionId],
        this.projectController.project.decisionsApprovedByGeneralMeeting[d.decisionId],
      ),
    );
  }

  get currentDocuments(): DocumentItem[] {
    return this.projectController.documents.map(toDocumentItem);
  }

  get documentLanguages(): Language[] {
    return getDocumentLanguages(this.projectController.project.jsonData.documents, this);
  }

  get documentStatuses(): ProjectDocumentStatus[] {
    return this.projectController.documentStatuses;
  }

  get isLocked(): boolean {
    return this.projectController.isLocked;
  }

  get fileUploadConfig(): FileUploadConfig {
    return this.projectParams.fileUploadConfig;
  }

  get boardMeetingFields(): BoardMeetingFields {
    return this.projectController.boardMeetingFields;
  }

  get generalMeetingFields(): GeneralMeetingFields {
    return this.projectController.generalMeetingFields;
  }

  get projectRoles(): ProjectCompanyRole[] {
    return this.projectController.project.jsonData.roles;
  }

  get orgNo(): string {
    return this.companyInfo.company.organizationnumber;
  }

  get projectId(): string {
    return this.projectController.projectId;
  }

  get user(): User {
    return this.projectController.initializationParams.user;
  }

  get boardMeetingNoticeRecipients(): Recipient[] {
    const form = findNoticeToBoardMeetingForm(this.projectController.documents);
    return form ? toRecipients(this.companyInfo.roles, form) : [];
  }

  get boardMeetingMinutesParticipants(): Recipient[] {
    const form = findBoardMinutesForm(this.projectController.documents);
    return form ? toRecipients(this.companyInfo.roles, form) : [];
  }

  get signingBoardMembers(): CompanyRoleIdWithName[] {
    return this.projectController.documents
      .map((d) => d.form)
      .flatMap((f) =>
        f.type === DocumentType.board_meeting ? f.selectedSigningBoardMembers.selected : [],
      );
  }

  get currentSubscription(): PaidSubscription | undefined {
    return this.projectParams.currentSubscription;
  }

  get subscriptionTypeModel(): SubscriptionTypeModel {
    return {
      currentSubscription: this.currentSubscription,
      requiredSubscriptionType: getRequiredSubscriptionType(
        this.currentDecisions.map((d) => d.type),
      ),
    };
  }

  get inputStates(): InputState[] {
    return this.projectController.project.jsonData.inputStates ?? [];
  }

  get aiClient(): AIClient {
    return this.projectParams.aiClient;
  }

  get projectStatus(): ProjectStatus {
    return this.projectController.project.projectStatus;
  }

  get isSharepointEnabled(): boolean {
    return this.projectController.project.isSharepointEnabled;
  }

  get locale() {
    return this.projectController.locale;
  }

  get detailsPanelMaximized() {
    return this.projectController.detailsPanelMaximized;
  }

  get boardMeetingVotes(): BoardMeetingVote[] {
    return this.projectController.project.boardMeetingVotes;
  }

  get validationErrors() {
    return this.projectController.validationErrors;
  }

  get displayMode() {
    return this.projectController.displayMode;
  }

  get participationList(): GeneralMeetingParticipation[] {
    return this.projectController.project.participationList;
  }

  get powerOfAttorneys(): PowerOfAttorneyForGeneralMeeting[] {
    return this.projectController.project.powerOfAttorneys;
  }

  get companies() {
    return this.projectParams.companies;
  }

  get companyContexts() {
    return {
      ...this.projectController.companyContexts,
      [this.orgNo]: ok(this),
    };
  }

  get batchData() {
    return this.projectController.project.batchData;
  }

  get isBatchProject() {
    return this.projectController.project.isBatchProject;
  }

  get documentSigners() {
    return this.projectController.documentSigners;
  }

  get state() {
    return this.projectController.state;
  }

  get jsonData() {
    return this.projectController.project.jsonData;
  }

  get hasSavedDefaultDocumentLanguage() {
    return (
      (this.projectParams.companyInfo.documentSettings?.defaultDocumentLanguages?.length ?? 0) > 0
    );
  }

  get isProd() {
    return isProductionEnvironment();
  }

  get featureToggles() {
    return featureTogglesForCompany(this.orgNo);
  }

  get subscribers() {
    return this.projectController.decisions.flatMap(
      (d) => getHandler(d.type).getSubscribers?.(d.form) ?? [],
    );
  }

  getData(req: DataRequest) {
    return this.projectController.project.data[this.orgNo]?.find((record) =>
      isEqual(record.request, req),
    )?.response;
  }

  addData(req: DataRequest) {
    this.projectController.data.push(req);
  }
}

export function getRequiredSubscriptionType(
  currentDecisions: DecisionType[],
): SubscriptionType | undefined {
  const uniqueSubscriptionTypes = new Set(
    currentDecisions.map((it) => {
      return categorySubscriptionMapping[categoryMapping[it]];
    }),
  );
  if (uniqueSubscriptionTypes.has(SubscriptionType.PRO)) {
    return SubscriptionType.PRO;
  } else if (uniqueSubscriptionTypes.has(SubscriptionType.BASIC)) {
    return SubscriptionType.BASIC;
  }
  return undefined;
}

export function updateDetailsPanelDocumentContext(
  documentId: string,
  detailsPanel: DetailsPanelRef | undefined,
  documents: DocumentItem[],
): DetailsPanelRef | undefined {
  const document = documents.find((d) => d.documentId === documentId);
  switch (document?.type) {
    case DocumentType.notice_general_meeting:
    case DocumentType.general_meeting:
    case DocumentType.notice_board_meeting:
    case DocumentType.board_meeting:
      switch (detailsPanel?.type) {
        case "decision":
          return {
            ...detailsPanel,
            documentId: documentId,
          };
      }
  }
}

type TabType = DocumentType | "review-matters" | "conduct-general-meeting";
type CustomTab = {
  type: "review-matters" | "conduct-general-meeting";
  content: { type: "custom"; content: ReactElement };
  documentId: string;
  title: TMessage;
  isRecipientPreview?: boolean;
  recipientLabel?: TMessage;
  nextRecipient?: () => void;
  prevRecipient?: () => void;
  recipientSelector?: SelectProps<RecipientId>;
  nextCompany?: () => void;
  prevCompany?: () => void;
  companySelector?: SelectProps<string>;
  download?: () => DownloadAsPdfButtonProps | undefined;
};
type DocumentTabContent = { type: "document"; documentId: string; content?: string };
type DocumentTab = {
  type: DocumentType;
  content?: DocumentTabContent[];
  documentId: string;
  title: TMessage;
  isRecipientPreview?: boolean;
  recipientLabel?: TMessage;
  nextRecipient?: () => void;
  prevRecipient?: () => void;
  recipientSelector?: SelectProps<RecipientId>;
  nextCompany?: () => void;
  prevCompany?: () => void;
  companySelector?: SelectProps<string>;
  download?: () => DownloadAsPdfButtonProps | undefined;
};
export type TabInPreview = CustomTab | DocumentTab;

export function isNonDocumentTab(tab?: TabType | string) {
  return (
    tab !== undefined &&
    (tab === "review-matters" ||
      tab === "conduct-general-meeting" ||
      tab === reviewDocumentId ||
      tab === conductGeneralMeetingDocumentId)
  );
}

export const shouldAddReviewTab = (
  documents: DocumentAndForm[],
  projectContext: ProjectContext,
): boolean => {
  const boardMinutes = documents.find((d) => d.type === DocumentType.board_meeting);
  if (boardMinutes !== undefined) {
    const minutesStatus = projectContext.documentStatuses.find(
      (d) => d.documentId === boardMinutes.documentId,
    )?.signingStatus;
    return (
      minutesStatus === DocumentSigningStatus.SENT_FOR_VOTING ||
      minutesStatus === DocumentSigningStatus.SENT_FOR_REVIEW
    );
  }
  return false;
};

export const shouldAddConductGeneralMeetingTab = (projectContext: ProjectContext): boolean => {
  return projectContext.generalMeetingFields.conductElectronicGeneralMeeting.value ?? false;
};

type JobItem = (done: () => void) => void;

export class JobQueue {
  items: JobItem[] = [];

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get isEmpty() {
    return this.items.length === 0;
  }

  push(item: JobItem) {
    this.items.push(item);
    if (this.items.length === 1) {
      this.run();
    }
  }

  run() {
    this.items[0]?.(this.onDone);
  }

  onDone() {
    this.items.shift();
    if (this.items.length) {
      this.run();
    }
  }
}

const getProjectName = (project: GetProjectResponse) =>
  project.name ?? project.generatedName ?? "???";

export class ProjectController {
  http: AxiosInstance;
  documentApiClient: DocumentApiClient;
  initializationParams: ProjectInitializationParams;
  locale = Language.EN;
  decisions: DecisionAndForm<DecisionType>[] = [];
  reactionDisposers: Map<string, CleanupFunction> = new Map<string, CleanupFunction>();
  documents: DocumentAndForm[] = [];
  project: GetProjectResponse;
  boardMeetingFields: BoardMeetingFields;
  generalMeetingFields: GeneralMeetingFields;
  documentSigners: Map<string, Result<GetProjectDocumentSignersResponse>> = new Map<
    string,
    Result<GetProjectDocumentSignersResponse>
  >();
  documentRecipientPreviews: {
    [key: string]: Result<DocumentPreview> | undefined;
  } = {};
  selectedRecipientId?: RecipientId;
  selectedCompany?: string;

  state: ProjectControllerState = readyState();
  saveQueue = new JobQueue();
  isSaveProjectScheduled = false;
  invalidDecisions: Set<string> = new Set();
  documentViewingStatuses: Map<string, DocumentViewingStatus[]> = new Map();
  documentSigningStatuses: Map<string, SigningStatusForSigner[]> = new Map();
  projectContext: ProjectContext;
  summaryController: SummaryController;

  companyContexts: { [orgNo: string]: Result<ProjectCompanyContext> | undefined } = {};

  from?: string;
  view: ProjectViewState = {};

  eventListeners: Array<(event: DbotEvent) => void> = [];

  readonly history: History;
  readonly orgNo: string;
  readonly accessType: AccessType;
  reviewController: MeetingEmailInitializerController;
  electronicGMController: AdminElectronicGeneralMeetingController;
  isDownloadingAttachment = false;
  validationErrors?: TMessage[];

  editedDecision?: {
    orgNo: string;
    decisionId: string;
    decisionType: DecisionType;
    decision: Result<DecisionAndForm<DecisionType>>;
    cleanup?: () => void;
  };

  nameGenerator?: (jsonData: ProjectJsonData) => string;
  replaceState: () => void = noop;
  data: DataRequest[] = [];

  constructor(
    project: GetProjectResponse,
    params: ProjectInitializationParams,
    http: AxiosInstance,
    history: History,
    accessType: AccessType,
  ) {
    makeAutoObservable(this, { http: false }, { autoBind: true });
    this.history = history;
    this.accessType = accessType;
    this.http = http;
    this.orgNo = params.companyInfo.company.organizationnumber;
    this.documentApiClient = new DocumentApiClient(http, this.orgNo);
    this.initializationParams = params;
    this.project = project;
    this.projectContext = new ContextWrapper(this, params);
    this.boardMeetingFields = createBoardMeetingFields({
      onChange: () => {
        logDebug("ProjectController: boardMeetingFields has changed");
        this.boardMeetingFields.isChanged = true;
        this.save();
      },
      jsonData: project.jsonData,
      aiClient: params.aiClient,
      isLocked: project.isLocked,
      defaultDocumentLanguage: defaultDocumentLanguage(
        params.companyInfo.company.organizationnumber,
      ),
    });
    this.generalMeetingFields = createGeneralMeetingFields({
      shareholders: this.projectContext.shareholderData.shareholders,
      roles: this.projectContext.companyInfo.roles,
      jsonData: this.project.jsonData,
      onChange: () => this.save(),
    });
    this.data = project.jsonData.data;
    project.jsonData.decisions.forEach(this.initDecision);
    project.jsonData.documents.forEach((d) => this.setupDocument(d));
    this.summaryController = new SummaryController(
      this.dispatch,
      this.projectContext,
      project.followUps,
    );
    this.reviewController = new MeetingEmailInitializerController(params.rootDispatch, http, noop);
    this.electronicGMController = new AdminElectronicGeneralMeetingController(
      http,
      this.dispatch,
      this.projectContext,
      this.project.jsonData,
      () => this.project.electronicGeneralMeeting,
    );
  }

  init({
    navigationState,
    nameGenerator,
    replaceState,
  }: {
    navigationState?: ProjectNavigationState;
    nameGenerator?: (jsonData: ProjectJsonData) => string;
    replaceState?: (state: ProjectNavigationState) => void;
  } = {}): CleanupFunction {
    logDebug("ProjectController::init", navigationState);
    this.nameGenerator = nameGenerator;
    this.replaceState = () => replaceState?.(toProjectNavigationState(this.from, toJS(this.view)));

    // Note: we are deferring the initialization of the document forms until the ProjectController.init() function is called
    this.documents.forEach((d) => {
      this.initDocument(d);
    });

    if (this.decisions.length === 1 && navigationState?.from === "create-new") {
      this.dispatch({
        type: "dbot/open-details-panel",
        panel: { type: "decision", decisionId: this.decisions[0].decisionId },
      });
    }

    if (navigationState?.from && navigationState?.from !== "create-new") {
      this.from = navigationState?.from;
    }

    if (navigationState?.overlay) {
      this.view.displayMode = "overlay";
    }

    if (navigationState?.view) {
      this.view = navigationState.view;
      this.initEditDecision();
    }

    if (navigationState?.documentId) {
      this.dispatch({
        type: "dbot/toggle-document-panel",
        documentId: navigationState?.documentId,
        expanded: true,
      });
    }

    this.initRecipientPreviewSync();
    this.replaceState();

    return () => {
      logDebug("ProjectController::cleanup", window.location.href);
      this.reactionDisposers.forEach((disposer) => {
        disposer();
      });
      this.electronicGMController.unsubscribeFromGeneralMeetingMessages();
    };
  }

  setViewState(data: Partial<ProjectViewState>) {
    Object.assign(this.view, data);
    this.replaceState();
  }

  initDocument(d: DocumentAndForm) {
    this.reactionDisposers.set(
      d.documentId,
      reaction(
        () => applyDocumentForm(d),
        () => {
          logDebug(`ProjectController: document ${d.type} ${d.documentId} has changed`);
          this.save();
        },
      ),
    );
    this.reactionDisposers.set(
      `${d.documentId}/overrides`,
      reaction(
        () =>
          this.projectContext.isBatchProject &&
          applyDocumentOverrides(d, this.project.batchCompanies),
        () => {
          logDebug(`ProjectController: document ${d.type} ${d.documentId} overrides has changed`);
          this.save();
        },
        { equals: comparer.structural },
      ),
    );
    this.reactionDisposers.set(
      `${d.documentId}/cleanup`,
      getDocumentHandler(d.type).initForm(d.form) ?? noop,
    );
  }

  cleanupDocument(documentId: string) {
    this.reactionDisposers.get(documentId)?.();
    this.reactionDisposers.delete(documentId);
    this.reactionDisposers.get(`${documentId}/form`)?.();
    this.reactionDisposers.delete(`${documentId}/form`);
  }

  setLocale(locale: Language) {
    this.locale = locale;
  }

  get projectId(): string {
    return this.project.projectId;
  }

  get name(): string {
    return getProjectName(this.project);
  }

  get isReadonly(): boolean {
    return this.accessType !== AccessType.ADMIN;
  }

  get isLocked(): boolean {
    return this.project.isLocked;
  }

  get version(): number {
    return this.project.version;
  }

  get documentStatuses() {
    return this.project.documentStatuses;
  }

  get previews() {
    return this.project.previews;
  }

  get visibleTabs(): TabId[] {
    return getVisibleTabs({
      decisions: this.summaryController.decisionsForApply,
      documents: this.documents,
      locale: this.projectContext.locale,
      conductElectronicGeneralMeeting:
        this.projectContext.generalMeetingFields?.conductElectronicGeneralMeeting.value,
      displayMode: this.displayMode,
      isBatchProject: this.project.isBatchProject,
    });
  }

  get activeTab(): TabId {
    return this.view.activeTab ?? this.visibleTabs[0];
  }

  get detailsPanel(): DetailsPanelRef | undefined {
    return this.view.detailsPanel;
  }

  get detailsPanelMaximized(): boolean {
    return this.view.detailsPanelMaximized ?? false;
  }

  get displayMode(): "normal" | "fullscreen" | "screen-share" | "overlay" {
    return this.view.displayMode ?? "normal";
  }

  get isMobileDocumentPreviewOpen(): boolean {
    return this.view.isMobileDocumentPreviewOpen ?? false;
  }

  get selectedDocumentId(): string | undefined {
    return this.view.selectedDocumentId ?? this.sortedDocuments[0].documentId;
  }

  get selectedDocumentPreview(): string | undefined {
    return this.view.selectedDocumentPreview ?? this.sortedDocuments[0].documentId;
  }

  get isFullscreen() {
    return this.displayMode === "fullscreen";
  }

  get isScreenShareMode() {
    return this.displayMode === "screen-share";
  }

  get isOverlayMode() {
    return this.displayMode === "overlay";
  }

  get containsNoticeBoardMeeting() {
    return this.documents.some((d) => d.type === DocumentType.notice_board_meeting);
  }

  get containsNoticeGeneralMeeting() {
    return this.documents.some((d) => d.type === DocumentType.notice_general_meeting);
  }

  get containsAnyBoardMeetingDocuments() {
    return this.documents.some((d) =>
      [DocumentType.notice_board_meeting, DocumentType.board_meeting].includes(d.type),
    );
  }

  get containsAnyGeneralMeetingDocuments() {
    return this.documents.some((d) =>
      [DocumentType.notice_general_meeting, DocumentType.general_meeting].includes(d.type),
    );
  }

  get isDuplicateGeneralMeeting() {
    return this.containsAnyGeneralMeetingDocuments && !this.containsAnyBoardMeetingDocuments;
  }

  get isDuplicateBoardMeeting() {
    return !this.containsAnyGeneralMeetingDocuments && this.containsAnyBoardMeetingDocuments;
  }

  get close() {
    const state = asProjectNavigationState(this.history.location.state);
    return state?.from && state.from !== "create-new"
      ? () => this.dispatch([{ type: "navigate", url: state.from! }])
      : undefined;
  }

  dispatch(event: DbotEvent | DCEvent | (DbotEvent | DCEvent)[]) {
    if (Array.isArray(event)) {
      event.forEach(this.handleEvent);
    } else {
      this.handleEvent(event);
    }
  }

  setProject(project: GetProjectResponse) {
    this.project = project;
    this.summaryController.followUps = project.followUps;
    this.dispatch(updateInputStateEvent({ inputStates: project.jsonData.inputStates ?? [] }));
    this.reviewController.reloadReview(findBoardMeetingMinutes(project.jsonData));
    if (project.isLocked) {
      this.dispatch([showSuccess({ key: "dbot.projectLocked" })]);
    }
    this.electronicGMController.setProjectJsonData(project.jsonData);
    this.documentRecipientPreviews = {};
    this.data = project.jsonData.data;
  }

  updateViewingStatus(documentId: string, viewingStatuses: DocumentViewingStatus[]) {
    this.documentViewingStatuses.set(documentId, viewingStatuses);
    const documentStatus = this.documentStatuses.find((d) => d.documentId === documentId);
    if (documentStatus) {
      documentStatus.numberOfViewers = viewingStatuses.length;
      documentStatus.numberOfViewed = viewingStatuses.filter(
        (x) => x.viewedTime !== undefined,
      ).length;
    }
  }

  updateSigningStatus(documentId: string, signingStatus: GetDocumentSigningStatusResponse) {
    this.documentSigningStatuses.set(documentId, signingStatus.signerStatus);
    const documentStatus = this.documentStatuses.find((d) => d.documentId === documentId);
    if (documentStatus) {
      documentStatus.signingStatus = signingStatus.documentSigningStatus;
      documentStatus.numberOfSigners = signingStatus.signerStatus.length;
      documentStatus.numberOfSignatures = signingStatus.signerStatus.filter(
        (x) => x.isSigned,
      ).length;
      documentStatus.numberOfSignedDocuments = signingStatus.numberOfSignedDocuments;
    }
  }

  handleEvent(event: DbotEvent | DCEvent) {
    if (!isDbotEvent(event)) {
      this.initializationParams.rootDispatch([event]);
      return;
    }

    logDebug("ProjectController::dispatch", event);
    const dispatch = deferred(this.dispatch);

    switch (event.type) {
      case "dbot/reload-project":
        this.history.replace(this.history.location.pathname, null); // clear any pushed state to ensure a "clean" reload
        window.location.reload();
        break;
      case "dbot/refresh-project-status":
        this.http
          .get<GetProjectResponse>(`project/${this.projectId}`)
          .then((res) => {
            this.setProject(res.data);
            dispatch(refreshProjectStatusSuccessEvent());
          })
          .catch((err) => {
            dispatch([showError({ key: "dbot.refreshProjectStatusFailure" })]);
            dispatch(refreshProjectStatusFailureEvent(err));
          });
        break;
      case "dbot/fetch-auditors":
        this.http
          .get("/company/list-auditors")
          .then((res) => res.data)
          .then((data) => dispatch({ type: "dbot/fetch-auditors-success", data }))
          .catch((error) => dispatch({ type: "dbot/fetch-auditors-error", error }));
        break;
      case "dbot/explore-articles-of-association":
        this.http
          .post(`/articleofassociation/categorize/${this.projectContext.orgNo}`, event.articles)
          .then((res) => res.data)
          .then((data) => dispatch({ type: "dbot/explore-articles-of-association-success", data }))
          .catch((error) =>
            dispatch({ type: "dbot/explore-articles-of-association-error", error }),
          );
        break;
      case "dbot/add-documents":
        this.save(event);
        break;
      case "dbot/add-decisions":
        this.save(event);
        break;
      case "dbot/copy-decisions":
        this.save(event);
        break;
      case "dbot/remove-decision":
        this.removeDecision(event.decisionId, event.onSuccess);
        break;
      case "dbot/register-new-signer":
        this.http
          .post<CompanyRole>(`/project/${this.projectId}/register-signer`, {
            name: event.name,
            email: event.email,
          })
          .then((res) => {
            runInAction(() => addCompanyRole(this.projectContext.companyInfo, res.data));
            dispatch(registerNewSignerSuccessEvent(res.data));
          })
          .catch(() => {
            dispatch(registerNewSignerFailureEvent({ key: "decisions.newSigner.saveError" }));
          });
        break;
      case "dbot/download-signed-document":
        this.http
          .get(`document/download-signed-project-documents/?ref=${event.documentId}`, {
            headers: { Accept: "*/*" },
            responseType: "blob",
          })
          .then((response: AxiosResponse) => {
            downloadFile(response.data, getFilenameFromHeaders(response.headers));
          })
          .catch(() => {
            dispatch([showGeneralError()]);
          });
        break;
      case "dbot/download-signed-subscription-forms":
        this.http
          .get(`document/download-signed-subscription-forms/?ref=${event.documentId}`, {
            headers: { Accept: "*/*" },
            responseType: "blob",
          })
          .then((response: AxiosResponse) => {
            downloadFile(response.data, getFilenameFromHeaders(response.headers));
          })
          .catch(() => {
            dispatch([showGeneralError()]);
          });
        break;
      case "dbot/get-signing-status":
        this.documentApiClient
          .getSigningStatus(event)
          .then((data) => {
            this.updateSigningStatus(event.documentId, data);
            dispatch(getSigningStatusSuccessEvent(event.documentId, data));
            dispatch(fetchDecisionStatusEvent());
          })
          .catch(() =>
            dispatch(
              getSigningStatusFailureEvent(event.documentId, {
                key: "dbot.getSigningStatusFailure",
              }),
            ),
          );
        break;
      case "dbot/fetch-increase-of-share-capital-subscribers-allocation":
        this.http
          .get<ShareAllocationRoundsResponse>(
            `/project/${this.projectId}/increaseOfShareCapitalAllocation`,
          )
          .then((res) => {
            dispatch(fetchIncreaseOfShareCapitalSubscribersAllocationSuccessEvent(res.data));
          })
          .catch(() =>
            dispatch(
              fetchIncreaseOfShareCapitalSubscribersAllocationFailureEvent({
                key: "dbot.fetchIncreaseOfShareCapitalSubscribersAllocationFailure",
              }),
            ),
          );
        break;
      case "dbot/save-share-allocation":
        this.http
          .post(`/project/${this.projectId}/increaseOfShareCapitalAllocation`, {
            allocationRounds: event.allocationRounds,
            isAllocationCompleted: event.isAllocationCompleted,
          })
          .then(() => {
            dispatch(saveShareAllocationSuccessEvent(event.isAllocationCompleted));
          })
          .catch(() => {
            dispatch([
              showError({
                key: "dbot.summary.subscribersAllocation.saveError",
              }),
            ]);
            dispatch(
              saveShareAllocationFailureEvent({
                key: "dbot.summary.subscribersAllocation.saveError",
              }),
            );
          });
        break;
      case "dbot/get-subscription-status":
        this.documentApiClient
          .getSubscriptionStatus(event)
          .then((data) => {
            dispatch(refreshProjectStatusEvent());
            dispatch(getSubscriptionStatusSuccessEvent(event.documentId, data));
          })
          .catch(() =>
            dispatch(
              getSubscriptionStatusFailureEvent(event.documentId, {
                key: "dbot.getSubscriptionStatusFailure",
              }),
            ),
          );
        break;
      case "dbot/get-viewing-status":
        this.http
          .get<GetDocumentViewingStatusResponse>(
            `/document/check-viewing-status/${event.documentId}`,
          )
          .then((res) => {
            this.updateViewingStatus(event.documentId, res.data.statusForViewers);
            dispatch(getViewingStatusSuccessEvent(event.documentId, res.data));
          })
          .catch(() =>
            dispatch(
              getViewingStatusFailureEvent(event.documentId, {
                key: "dbot.getViewingStatusFailure",
              }),
            ),
          );
        break;
      case "dbot/send-document-for-viewing":
        this.http
          .post(
            "/document/send-for-viewing",
            sendDocumentsForViewingRequest({
              organizationnumber: this.projectContext.orgNo,
              projectId: this.projectId,
              documentTypes: this.documents
                .filter((d) => d.documentId === event.documentId)
                .map((d) => d.type),
              documentId: event.documentId,
              emailBody: event.emailBody,
              authMethod: event.authMethod,
              password: event.password,
              recipients: event.recipients,
            }),
          )
          .then(() => {
            dispatch([
              showSuccess({ key: "dbot.sendDocumentForViewingSuccess" }, { autoHide: true }),
            ]);
            dispatch(sendDocumentForViewingSuccessEvent(event.documentId));
            dispatch(refreshProjectStatusEvent());
            event.onSuccess?.();
          })
          .catch(() => {
            dispatch([showError({ key: "dbot.sendDocumentForViewingFailure" })]);
            dispatch(
              sendDocumentForViewingFailureEvent(event.documentId, {
                key: "dbot.sendDocumentForViewingFailure",
              }),
            );
            event.onFailure?.();
          });
        break;
      case "dbot/resend-notice-of-board-meeting":
        this.http
          .post(
            "/document/resend-notice-board-meeting",
            resendNoticeOfBoardMeetingRequest({
              organizationnumber: this.projectContext.orgNo,
              projectId: this.projectId,
              emailBody: event.emailBody,
            }),
          )
          .then(() => {
            dispatch([
              showSuccess({ key: "dbot.sendDocumentForViewingSuccess" }, { autoHide: true }),
            ]);
            dispatch(resendNoticeOfBoardMeetingSuccessEvent(event.document));
            dispatch(refreshProjectStatusEvent());
          })
          .catch(() => {
            dispatch([showError({ key: "dbot.sendDocumentForViewingFailure" })]);
            dispatch(
              resendNoticeOfBoardMeetingFailureEvent(event.document, {
                key: "dbot.sendDocumentForViewingFailure",
              }),
            );
          });
        break;
      case "dbot/resend-notice-to-updated-recipient":
        this.http
          .post(`/document/resend-for-viewing/${event.documentId}`, event.data)
          .then(() => {
            if (event.data.companyroleId) {
              applyCompanyRoleEmails(this.projectContext, [
                { companyroleId: event.data.companyroleId, email: event.data.recipientEmail },
              ]);
            }
            dispatch({
              ...event,
              type: "dbot/resend-notice-to-updated-recipient-result",
              result: ok(),
            });
          })
          .catch(() => {
            dispatch({
              ...event,
              type: "dbot/resend-notice-to-updated-recipient-result",
              result: failure({ key: "dbot.sendDocumentForViewingFailure" }),
            });
          });
        break;
      case "dbot/send-subscription-form-for-signing":
        this.http
          .post(
            "/document/send-subscription-form-for-signing",
            mapToSendForSigningRequest(this.projectId, event),
          )
          .then(() => {
            dispatch([
              showSuccess({ key: "dbot.sendDocumentForViewingSuccess" }, { autoHide: true }),
            ]);
            dispatch(sendSubscriptionFormForSigningResult(event, ok()));
            dispatch(refreshProjectStatusEvent());
          })
          .catch(() => {
            dispatch([showError({ key: "dbot.sendDocumentForSigningFailure" })]);
            dispatch(sendSubscriptionFormForSigningResult(event, failure()));
          });
        break;
      case "dbot/send-document-for-signing":
        this.http
          .post(
            event.document.type === DocumentType.custom_document
              ? "document/send-custom-document-for-signing"
              : "/document/send-for-signing",
            mapToSendForSigningRequest(
              this.projectId,
              event,
              event.signingSettings,
              event.emailBody,
            ),
          )
          .then(() => {
            dispatch(
              sendDocumentForSigningResultEvent(
                event,
                ok(null),
                event.emailBody,
                this.projectContext.userPrefs,
              ),
            );
            dispatch(refreshProjectStatusEvent());
            dispatch(getSigningStatusEvent(event.document.documentId));
          })
          .catch(() => {
            dispatch([showError({ key: "dbot.sendDocumentForSigningFailure" })]);
            dispatch(sendDocumentForSigningResultEvent(event, failure(), event.emailBody));
          });
        break;
      case "dbot/resend-document-for-signing":
        this.documentApiClient
          .resendForSigning(event, event)
          .then((data) => {
            dispatch({
              type: "dbot/resend-document-for-signing-result",
              documentId: event.documentId,
              signedDocumentId: event.signedDocumentId,
              result: ok(data),
            });
          })
          .catch(() => {
            dispatch({
              type: "dbot/resend-document-for-signing-result",
              documentId: event.documentId,
              signedDocumentId: event.signedDocumentId,
              result: failure({ key: "dbot.summary.resendFailure" }),
            });
          });
        break;
      case "dbot/update-company-role-emails":
        this.updateCompanyRoles(event.orgNo, event.items)
          .then(() =>
            dispatch(
              updateCompanyRoleEmailsSuccessEvent(event.orgNo, event.items, {
                key: "dbot.recipient-email-updated.company-role",
              }),
            ),
          )
          .catch(() =>
            dispatch(
              updateCompanyRoleEmailsFailureEvent(event.orgNo, event.items, {
                key: "dbot.updateCompanyRoleEmailsFailure",
              }),
            ),
          );
        break;
      case "dbot/update-decision-texts":
        {
          const decision = this.decisions.find((d) => d.decisionId === event.decisionId);
          if (decision && event.texts.length) {
            event.texts.forEach((t) => {
              replaceLocalizedTextForDecision(t, decision);
            });
            this.save();
          }
        }
        break;
      case "dbot/fetch-decision-status":
        this.http
          .get<DecisionStatusResponse>(`/project/${this.projectId}/decisionStatus`)
          .then((res) => {
            dispatch(fetchDecisionStatusResultEvent(res.data));
          })
          .catch(() => {
            dispatch([showError({ key: "dbot.summary.fetchDecisionStatusFailure" })]);
          });
        break;
      case "dbot/update-follow-up-status":
        this.http
          .post(
            `/project/${this.projectId}/decisionFollowUp/${event.decisionId}`,
            event.followUpData,
          )
          .then((res) => {
            dispatch(updateFollowUpStatusResultEvent(res.data));
          })
          .catch(() => {
            dispatch([showError({ key: "dbot.summary.fetchDecisionStatusFailure" })]);
          });
        break;
      case "dbot/apply-decision":
        this.http
          .post(`/project/${this.projectId}/apply-decision`, null, {
            params: {
              decisionId: event.decisionId,
            },
          })
          .then(() => {
            dispatch(applyDecisionSuccessEvent(event.decisionId));
            dispatch({ type: "dbot/reload-project" });
          })
          .catch(() => {
            dispatch([showError({ key: "dbot.summary.applyDecisionFailure" })]);
            dispatch(
              applyDecisionFailureEvent(event.decisionId, {
                key: "dbot.summary.applyDecisionFailure",
              }),
            );
          });
        break;
      case "dbot/send-to-br":
        this.http
          .post(`/project/${this.projectId}/send-to-br`, {
            decisionId: event.decisionId,
            signer: event.signer,
          })
          .then(() => {
            dispatch(fetchDecisionStatusEvent());
            dispatch(sendToBRSuccessEvent(event.decisionId));
          })
          .catch(() => {
            dispatch([showError({ key: "dbot.summary.sendToBRFailure" })]);
            dispatch(
              sendToBRFailureEvent(event.decisionId, { key: "dbot.summary.sendToBRFailure" }),
            );
          });
        break;
      case "dbot/save-participation":
        this.http
          .post(`/project/${this.projectId}/generalMeetingParticipation`, {
            createParticipation: event.createParticipation,
            updateParticipation: event.updateParticipation,
          })
          .catch(() => {
            dispatch([
              showError({
                key: "documents.attendingShareholders.participationSaveError",
              }),
            ]);
          });
        break;
      case "dbot/upload-attachments":
        uploadAttachments(
          this.projectId,
          event,
          this.initializationParams.fileUploadConfig,
          dispatch,
          this.http,
          this.documents,
          this.decisions,
        );
        break;
      case "dbot/delete-attachment":
        deleteAttachment(
          this.projectId,
          event,
          dispatch,
          this.http,
          this.documents,
          this.decisions,
        );
        break;
      case "dbot/download-attachment":
        downloadAttachment({
          projectId: this.projectId,
          attachmentId: event.attachmentId,
          http: this.http,
        })
          .then(({ data, filename }) => downloadFile(data, filename))
          .catch(() => {
            dispatch([showGeneralError()]);
          });
        break;
      case "dbot/open-attachment":
        if (this.isDownloadingAttachment) {
          return;
        }
        if (event.filename) {
          this.dispatch([
            {
              type: "preview-file",
              filename: event.filename,
              file: {
                url: projectApi.downloadAttachmentUrl({
                  projectId: this.projectId,
                  attachmentId: event.attachmentId,
                }),
              },
            },
          ]);
        } else {
          this.isDownloadingAttachment = true;
          downloadAttachment({
            projectId: this.projectId,
            attachmentId: event.attachmentId,
            http: this.http,
          })
            .then(({ data, filename }) => {
              this.dispatch([
                {
                  type: "preview-file",
                  filename,
                  file: { data },
                },
              ]);
            })
            .catch(() => {
              dispatch([showGeneralError()]);
            })
            .finally(
              action(() => {
                this.isDownloadingAttachment = false;
              }),
            );
        }
        break;
      case "dbot/fetch-general-meeting-votes":
        this.http
          .get(`/project/${this.projectId}/general-meeting-voting`)
          .then((response) => dispatch(fetchVotingResultEvent(response.data)))
          .catch(() => {
            dispatch([showError({ key: "documents.generalMeetingVoting.fetchError" })]);
          });
        break;
      case "dbot/save-general-meeting-votes":
        if (event.voteType === "decision") {
          this.http
            .post(
              `/project/${this.projectId}/general-meeting-voting/decision/${event.decisionId}`,
              event.votes,
            )
            .then(() => dispatch(saveGeneralMeetingVotesSuccessEvent()))
            .catch(() => {
              dispatch(saveGeneralMeetingVotesErrorEvent());
            });
        } else {
          this.http
            .post(
              `/project/${this.projectId}/general-meeting-voting/${event.voteType}`,
              event.votes,
            )
            .then(() => dispatch(saveGeneralMeetingVotesSuccessEvent()))
            .catch(() => {
              dispatch(saveGeneralMeetingVotesErrorEvent());
            });
        }
        break;
      case "dbot/search-companies":
        if (event.input.length > 2) {
          this.http
            .get<SuggestedCompany[]>(`/companysearch`, { params: { q: event.input } })
            .then((response) => dispatch(searchCompaniesResultEvent(event.source, response.data)))
            .catch(() => {
              dispatch(searchCompaniesResultEvent(event.source));
            });
        } else {
          dispatch(searchCompaniesResultEvent(event.source, []));
        }
        break;
      case "dbot/update-board-meeting-recipients": {
        this.updateCompanyRoles(
          this.orgNo,
          getUpdatedRoles(this.projectContext.companyInfo.roles, event.items),
        )
          .then(() => dispatch(updateBoardMeetingRecipientsSuccessEvent(event.items)))
          .catch(() =>
            dispatch(
              updateBoardMeetingRecipientsFailureEvent({
                key: "dbot.updateBoardMeetingRecipientsFailure",
              }),
            ),
          );
        break;
      }
      case "dbot/update-shareholder-email": {
        this.updateShareholderEmail(event.shareholderId, event.email)
          .then(() =>
            event.onFulfilled(ok({ key: "dbot.recipient-email-updated.shareholder" }), event.email),
          )
          .catch(() => {
            event.onFulfilled(failure({ key: "error.update-email" }));
          });
        break;
      }

      case "dbot/validate-project":
        this.validateProject(event);
        break;
      case "dbot/reopen-document":
        this.http
          .post(`/document/reopen-locked-document/${event.documentId}`, {})
          .then(() => {
            dispatch(reopenDocumentResultEvent(event.documentId, ok()));
            dispatch([showSuccess({ key: "dbot.action.reopen.success" })]);
          })
          .catch(() => {
            dispatch(reopenDocumentResultEvent(event.documentId, failure()));
          });
        break;
      case "dbot/send-for-review":
        this.documentApiClient
          .sendForReview({ documentId: event.documentId, data: event })
          .then(() => {
            dispatch({ type: "dbot/send-for-review-result", result: ok() });
            dispatch(refreshProjectStatusEvent());
            dispatch({
              type: "dbot/toggle-document-panel",
              documentId: reviewDocumentId,
              expanded: true,
            });
          })
          .catch(() => {
            dispatch({
              type: "dbot/send-for-review-result",
              result: failure(),
            });
          });
        break;
      case "dbot/autosign-document":
        this.state = { type: "loading", message: { translated: "Autosigning documents" } };
        this.http
          .post(`/document/autosign/${event.document.documentId}`, {})
          .then(() => dispatch({ type: "dbot/reload-project" }))
          .catch(() => {
            dispatch({ type: "dbot/reload-project" });
          });
        break;
      case "dbot/autoapprove-send-to-br":
        this.state = {
          type: "loading",
          message: { translated: "Autoapproving decision sent to BR" },
        };
        this.http
          .post(`/project/${this.projectId}/autoapprove-send-to-br/${event.decisionId}`, {})
          .then(() => dispatch({ type: "dbot/reload-project" }))
          .catch(() => {
            dispatch({ type: "dbot/reload-project" });
          });
        break;
      case "dbot/navigate-to-project-list":
        navigateToProjectList(this.orgNo, this.history);
        break;
      case "dbot/edit-project-name":
        this.dispatch([
          {
            type: "editProjectName",
            name: new StringFieldModel({ value: this.name, required: true }),
            save: (name: string) => dispatch({ type: "dbot/save-project-name", name: name }),
          },
        ]);
        break;
      case "dbot/save-project":
        this.save(event);
        break;
      case "dbot/save-project-name":
        if (event.name !== this.project.name && event.name !== "") {
          this.project.name = event.name;
          this.save();
        }
        this.dispatch([{ type: "closeDialog" }]);
        break;
      case "dbot/open-mobile-document-preview":
        this.setViewState({ isMobileDocumentPreviewOpen: true });
        break;
      case "dbot/close-mobile-document-preview":
        this.setViewState({ isMobileDocumentPreviewOpen: false });
        break;
      case "dbot/duplicate-project":
        this.dispatch([
          this.isDuplicateGeneralMeeting || this.isDuplicateBoardMeeting
            ? {
                type: "new-meeting",
                orgNo: this.orgNo,
                meetingType: this.isDuplicateBoardMeeting
                  ? MeetingType.BOARD_MEETING
                  : MeetingType.GENERAL_MEETING,
                projectName: { translated: event.name },
                projectId: this.projectId,
              }
            : {
                type: "editProjectName",
                name: new StringFieldModel({ value: event.name, required: true }),
                save: (name: string) =>
                  dispatch({ type: "dbot/confirm-duplicate-project", name: name }),
                isDuplicate: true,
              },
        ]);
        break;
      case "dbot/confirm-duplicate-project":
        this.http
          .post(`/projects`, mapToCreateProjectRequest(this.orgNo, event.name, this.projectId))
          .then((res) =>
            navigateToProject({
              orgNo: this.orgNo,
              projectId: res.data.projectId,
              history: this.history,
              state: { type: "project-navigation-state", from: "create-new" },
            }),
          )
          .catch(() => {
            dispatch([showGeneralError()]);
          });
        break;
      case "dbot/delete-project":
        this.state = { type: "delete-project", alert: event.alert, onSuccess: event.onSuccess };
        break;
      case "dbot/cancel-delete-project":
        this.state = readyState();
        break;
      case "dbot/confirm-delete-project":
        if (this.state.type === "delete-project") {
          this.state.deleteResult = pending();
          const onSuccess = this.state.onSuccess;
          this.http
            .delete(`/project/${this.projectId}`)
            .then(() => dispatch(onSuccess ?? { type: "dbot/navigate-to-project-list" }))
            .catch(() => {
              dispatch([showGeneralError()]);
            });
        }
        break;
      case "dbot/toggle-fullscreen":
        this.setViewState({
          displayMode: this.displayMode === "fullscreen" ? "normal" : "fullscreen",
        });
        break;
      case "dbot/toggle-screen-share-mode":
        this.setViewState({
          displayMode: this.displayMode === "screen-share" ? "normal" : "screen-share",
        });
        break;
      case "dbot/open-details-panel":
        this.setViewState({ detailsPanel: event.panel });
        this.initEditDecision();
        break;
      case "dbot/close-details-panel":
        this.setViewState({ detailsPanel: undefined, detailsPanelMaximized: false });
        this.cleanupEditDecision();
        this.cleanupEditDocument();
        break;
      case "dbot/move-decision":
        this.moveDecision(event.decisionId, event.toDecisionId);
        break;
      case "dbot/open-tab":
        this.setViewState({ activeTab: event.tab });
        if (this.activeTab === "meetings") {
          this.dispatch({
            type: "dbot/toggle-document-panel",
            documentId: conductGeneralMeetingDocumentId,
            expanded: true,
          });
        }
        break;
      case "dbot/toggle-document-panel":
        this.setViewState({
          selectedDocumentId: isNonDocumentTab(event.documentId)
            ? this.view.selectedDocumentId
            : event.expanded
              ? event.documentId
              : undefined,
          selectedDocumentPreview: event.documentId,
          detailsPanel: updateDetailsPanelDocumentContext(
            event.documentId,
            this.detailsPanel,
            this.projectContext.currentDocuments,
          ),
          activeTab:
            event.documentId === conductGeneralMeetingDocumentId ? "meetings" : "documents",
        });
        break;
      case "dbot/toggle-maximize-details-panel":
        this.setViewState({ detailsPanelMaximized: !this.detailsPanelMaximized });
        break;
      case "dbot/electronic-general-meeting-update-status":
        this.electronicGMController.updateStatus(event);
        break;
      case "dbot/electronic-general-meeting-update-status-result":
        if (isOk(event.result)) {
          this.electronicGMController.updateMeeting(event.result.data);
        } else {
          dispatch([showGeneralError()]);
        }
        break;
      case "dbot/update-general-meeting":
        this.project.electronicGeneralMeeting = event.meeting;
        break;
      case "dbot/project-validation-failed":
        this.validationErrors = [event.validationError];
        break;

      case "dbot/change-state":
        this.state = event.state;
        break;

      case "dbot/update-state":
        runInAction(() => event.update(this.state));
        break;

      case "dbot/set-project-data":
        this.setProject(event.data);
        break;

      case "dbot/save-selected-batch-companies":
        this.http
          .post(`/project/${this.projectId}/select-batch-companies`, event.body)
          .then((res) => this.dispatch(event.onSuccess(res.data)))
          .catch(() => {
            this.dispatch(event.onFailure);
          });
        break;

      case "dbot/fetch-document-signers":
        this.documentSigners.set(event.documentId, pending());
        this.http
          .get<GetProjectDocumentSignersResponse>(
            `/project/${this.projectId}/signers/${event.documentId}`,
          )
          .then(
            action((res) => {
              this.documentSigners.set(event.documentId, ok(res.data));
              this.dispatch(event.onSuccess(res.data));
            }),
          )
          .catch(
            action(() => {
              this.documentSigners.set(event.documentId, failure());
              this.dispatch(event.onFailure);
            }),
          );
        break;

      case "dbot/confirm-remove-document":
        this.confirmRemoveDocument(event);
        break;

      case "dbot/open-change-permission-dialog":
        this.dispatch({
          type: "showChangePermissionDialog",
          title: this.name,
          objectId: toProjectReference(this.projectId),
          onUpdate: (permissions: ObjectPermission[]) =>
            (this.project.numberOfPermissions = permissions.length),
        });
        break;

      case "dbot/fetch-projects-for-copy":
        this.http
          .get<ProjectsForCopyResponse>(
            `/projects/${this.orgNo}/projects-for-copy/${event.projectId}`,
          )
          .then((res) => this.dispatch(event.onSuccess(res.data.projects)))
          .catch(() => this.dispatch(event.onFailure));
        break;

      case "dbot/close-dialog":
        this.summaryController.closeDialog();
        break;

      case "dbot/fetch-company-context-data":
        if (!this.companyContexts[event.orgNo]) {
          this.companyContexts[event.orgNo] = pending();
          Promise.all([
            this.http.get<GetShareholdersResponse>(`/shareholder/all/${event.orgNo}`),
            this.http.get<CompanyInfo>(`/company/${event.orgNo}`),
          ])
            .then(
              action(([shareholderDataResponse, companyDataResponse]) => {
                this.companyContexts[event.orgNo] = ok({
                  companyInfo: companyDataResponse.data,
                  shareholderData: prepShareholderData(event.orgNo, shareholderDataResponse.data),
                });
              }),
            )
            .catch(
              action(() => {
                this.companyContexts[event.orgNo] = failure();
              }),
            );
        }
        break;

      case "dbot/select-company":
        this.selectedCompany = event.orgNo;
        break;
    }

    forwardEvent(
      event,
      this.decisions,
      this.documents,
      this.summaryController,
      this.electronicGMController,
      this.eventListeners,
    );
  }

  updateCompanyRoles(orgNo: string, items: UpdateCompanyRoleItem[]): Promise<void> {
    if (!items.length) {
      return Promise.resolve();
    }
    return this.http
      .post(`/company/update-companyrole-emails/${orgNo}`, items)
      .then(action(() => applyCompanyRoleEmails(this.projectContext, items)));
  }

  updateShareholderEmail(shareholderId: number, email: string): Promise<void> {
    return this.http
      .post(`/shareholder/update`, {
        shareholderId: shareholderId,
        fieldName: ShareholderField.email,
        newValue: email,
      })
      .then(action(() => applyShareholderEmail(this.projectContext, shareholderId, email)));
  }

  initDecision(decision: Decision, index?: number): DecisionAndForm<DecisionType> {
    const context = createDecisionContext(
      decision.decisionId,
      decision.decisionTexts,
      decision.attachments,
      this,
    );
    const decisionDataProvider = () =>
      this.project.jsonData.decisions.find((x) => x.decisionId === decision.decisionId)!;
    const d: DecisionAndForm<DecisionType> = {
      type: decision.decisionData.type,
      decisionId: decision.decisionId,
      get decision() {
        return decisionDataProvider();
      },
      form: getHandler(decision.decisionData.type).initNewForm(
        this.dispatch,
        context,
        decision.decisionData,
        decision,
      ),
      decisionTexts: context.decisionTexts,
      attachments: context.attachments,
    };
    if (index === undefined) {
      this.decisions.push(d);
    } else {
      this.decisions.splice(index, 0, d);
    }

    const observedDecision = this.decisions.find((x) => x.decisionId === d.decisionId)!;

    this.reactionDisposers.set(
      d.decisionId,
      reaction(
        () => applyForm(observedDecision),
        () => {
          logDebug(`ProjectController: decision ${d.type} (${d.decisionId}) has changed`);
          this.save();
        },
      ),
    );

    return d;
  }

  removeDecision(decisionId: string, onSuccess?: DbotEvent) {
    this.state = removeDecisionState(decisionId, onSuccess);
  }

  moveDecision(decisionId: string, toDecisionId: string) {
    const decision = this.decisions.find((d) => d.decisionId === decisionId);
    if (decision) {
      const toIndex = this.decisions.findIndex((d) => d.decisionId === toDecisionId);
      this.decisions = move(this.decisions, decision, toIndex);
      this.save();
    }
  }

  *reloadGeneralMeeting() {
    try {
      this.project.electronicGeneralMeeting = yield this.http
        .get(`/general-meeting/${this.projectId}`)
        .then((r) => r.data);
    } catch (e) {
      this.dispatch([showGeneralError()]);
    }
  }

  get showConfirmRemoveDecisionDialog() {
    return isRemoveDecision(this.state) || isRemovingDecision(this.state);
  }

  confirmRemoveDecision() {
    if (!isRemoveDecision(this.state)) {
      return;
    }
    const onSuccess = this.state.onSuccess;
    this.state = removingDecisionState(this.state);

    this.reactionDisposers.get(this.state.decisionId)?.();
    this.reactionDisposers.delete(this.state.decisionId);

    const removed = removeDecisionById(this.decisions, this.state.decisionId);

    if (removed) {
      this.save();
      this.dispatch({ type: "dbot/decision-removed", decisionId: this.state.decisionId });
      if (onSuccess) {
        this.dispatch(onSuccess);
      }
    }

    this.state = readyState();
  }

  cancelRemoveDecision() {
    this.state = readyState();
  }

  get sortedDocuments(): DocumentAndForm[] {
    return sortDocuments(this.documents);
  }

  getRecipientPreview(
    documentId: string,
    company: string | undefined,
    recipientId: RecipientId | undefined,
  ): Result<DocumentPreview> | undefined {
    return this.documentRecipientPreviews[compact([documentId, company, recipientId]).join("/")];
  }

  setRecipientPreview(
    documentId: string,
    company: string | undefined,
    recipientId: RecipientId | undefined,
    preview: Result<DocumentPreview>,
  ) {
    this.documentRecipientPreviews[compact([documentId, company, recipientId]).join("/")] = preview;
  }

  get sortedDocumentPreviews(): TabInPreview[] {
    return this.sortedDocuments.map<DocumentTab>((d) => {
      const companies = getProjectCompanies(this.projectContext);
      const selectedCompany =
        companies.find((c) => c.orgNo === this.selectedCompany)?.orgNo ?? companies[0].orgNo;
      const nextCompany = next(companies, (c) => c.orgNo === selectedCompany)?.orgNo;
      const prevCompany = prev(companies, (c) => c.orgNo === selectedCompany)?.orgNo;
      const recipients = getDocumentHandler(d.type).getRecipients?.(d.form, selectedCompany) ?? [];
      const selectedRecipientId =
        recipients.find((r) => r.id === this.selectedRecipientId)?.id ?? recipients?.[0]?.id;
      const nextRecipientId = next(recipients, (r) => r.id === selectedRecipientId)?.id;
      const prevRecipientId = prev(recipients, (r) => r.id === selectedRecipientId)?.id;
      const recipientIndex = recipients.findIndex((r) => r.id === selectedRecipientId);
      const recipientPreview = this.getRecipientPreview(
        d.documentId,
        selectedCompany,
        selectedRecipientId,
      );
      const preview: DocumentPreview | undefined = isOk(recipientPreview)
        ? recipientPreview.data
        : this.previews.find((p) => p.documentId === d.documentId);
      const batchPreviews = compact(
        sortedCompanies(this.projectContext.companies).map((c) => preview?.batchPreviews[c.orgNo]),
      );
      return {
        type: d.type,
        content: !preview
          ? undefined
          : [
              {
                type: "document",
                content: prepareDocumentPreview(preview),
                documentId: d.documentId,
              },
              ...(recipientPreview !== undefined
                ? []
                : batchPreviews.map<DocumentTabContent>((p) => ({
                    type: "document",
                    content: prepareDocumentPreview(p),
                    documentId: p.documentId,
                  }))),
            ],
        documentId: d.documentId,
        title: getProjectDocumentTitle(d, this.projectContext.locale),
        isRecipientPreview: recipientPreview !== undefined,
        recipientLabel: {
          key: "dbot.previewing-recipient",
          params: {
            n: recipientIndex + 1,
            m: recipients.length,
          },
        },
        nextRecipient: nextRecipientId
          ? action(() => {
              this.selectedRecipientId = nextRecipientId;
            })
          : undefined,
        prevRecipient: prevRecipientId
          ? action(() => {
              this.selectedRecipientId = prevRecipientId;
            })
          : undefined,
        recipientSelector: {
          value: selectedRecipientId,
          items: recipients.map((r) => r.id),
          idFn: identity,
          labelFn: (id) => recipients.find((r) => r.id === id)?.name ?? "",
          onSelect: action((id) => {
            this.selectedRecipientId = id;
          }),
        },
        nextCompany: nextCompany
          ? action(() => {
              this.selectedCompany = nextCompany;
            })
          : undefined,
        prevCompany: prevCompany
          ? action(() => {
              this.selectedCompany = prevCompany;
            })
          : undefined,
        companySelector: this.projectContext.isBatchProject
          ? {
              value: selectedCompany,
              items: companies.map((r) => r.orgNo),
              idFn: identity,
              labelFn: (orgNo) => companies.find((c) => c.orgNo === orgNo)?.name ?? "",
              onSelect: action((orgNo) => {
                this.selectedCompany = orgNo;
              }),
            }
          : undefined,
        download: () => ({
          onDownload: () => {
            if (requiresSubscription(this.projectContext.subscriptionTypeModel)) {
              this.dispatch([showPaymentDialog]);
            } else {
              this.downloadDocument(d.documentId, selectedCompany, selectedRecipientId);
            }
          },
          documentType: d.type,
        }),
      };
    });
  }

  initRecipientPreviewSync() {
    this.reactionDisposers.set(
      "recipient-preview",
      reaction(
        () => {
          const preview = this.sortedDocumentPreviews.find(
            (p) => p.documentId === this.selectedDocumentPreview,
          );
          const document = this.documents.find((d) => d.documentId === preview?.documentId);
          const companies = getProjectCompanies(this.projectContext);
          const selectedCompany =
            companies.find((c) => c.orgNo === this.selectedCompany)?.orgNo ?? companies[0].orgNo;
          const recipients =
            when(document, (d) =>
              getDocumentHandler(d.type).getRecipients?.(d.form, selectedCompany),
            ) ?? [];
          const selectedRecipientId =
            recipients?.find((r) => r.id === this.selectedRecipientId)?.id ?? recipients[0]?.id;
          return [document?.documentId, selectedCompany, selectedRecipientId] as const;
        },
        ([documentId, selectedCompany, selectedRecipientId]) => {
          if (documentId && selectedCompany && selectedRecipientId) {
            this.updateRecipientPreview(documentId, selectedCompany, selectedRecipientId);
          }
        },
        { fireImmediately: true },
      ),
    );
  }

  updateRecipientPreview(
    documentId: string,
    selectedCompany: string,
    selectedRecipientId: RecipientId,
  ) {
    if (!this.getRecipientPreview(documentId, selectedCompany, selectedRecipientId)) {
      this.setRecipientPreview(documentId, selectedCompany, selectedRecipientId, pending());
      this.http
        .get(
          `/project/${this.projectId}/preview-document/${documentId}?recipientId=${selectedRecipientId}&orgNo=${selectedCompany}`,
        )
        .then((res) => {
          this.setRecipientPreview(documentId, selectedCompany, selectedRecipientId, ok(res.data));
        })
        .catch(() => {
          this.setRecipientPreview(documentId, selectedCompany, selectedRecipientId, failure());
        });
    }
  }

  get documentTabs(): TabInPreview[] {
    let sorted = this.sortedDocumentPreviews;
    if (shouldAddReviewTab(this.documents, this.projectContext)) {
      sorted = this.withReviewTab(sorted);
    }
    if (shouldAddConductGeneralMeetingTab(this.projectContext)) {
      sorted = this.withConductElectronicGeneralMeetingTab(sorted);
    }
    return sorted;
  }

  private withReviewTab(sorted: TabInPreview[]): TabInPreview[] {
    const index = sorted.findIndex((d) => d.type === DocumentType.board_meeting);
    const documentId = sorted[index].documentId;
    return sorted.toSpliced(index, 0, {
      type: "review-matters",
      content: {
        type: "custom",
        content: (
          <ReviewMinutesAdminInitializer
            documentId={documentId}
            controller={this.reviewController}
          />
        ),
      },
      documentId: reviewDocumentId,
      title: { key: "review-matters" },
    });
  }

  private withConductElectronicGeneralMeetingTab(sorted: TabInPreview[]): TabInPreview[] {
    const index = sorted.findIndex((d) => d.type === DocumentType.notice_general_meeting);
    return sorted.toSpliced(index + 1, 0, {
      type: "conduct-general-meeting",
      content: {
        type: "custom",
        content: (
          <AdminElectronicGeneralMeetingInitializer
            projectId={this.projectContext.projectId}
            controller={this.electronicGMController}
          />
        ),
      },
      documentId: conductGeneralMeetingDocumentId,
      title: { key: "conduct-general-meeting" },
    });
  }

  downloadDocument(documentId: string, orgNo?: string, recipientId?: RecipientId): Promise<void> {
    return downloadDocument(
      `/project/${this.projectId}/download-document/${documentId}${toQueryString({ orgNo, recipientId })}`,
      this.http,
    );
  }

  setupDocument(document: Document, options?: Record<string, unknown>): DocumentAndForm {
    const context = createDocumentContext(document.documentId, this);
    const d: DocumentAndForm = {
      documentId: document.documentId,
      type: document.documentData.type,
      form: getDocumentHandler(document.documentData.type).initNewForm(
        this.dispatch,
        context,
        options,
      ),
      attachments: document.attachments ?? [],
    };
    this.documents.push(d);
    // Ensure to return the now observed document instead of `d` directly
    return this.documents.find((d) => d.documentId === document.documentId)!;
  }

  isDocumentLocked(documentId: string): boolean {
    return isDocumentLocked(this, documentId);
  }

  confirmRemoveDocument(event: ConfirmRemoveDocumentEvent) {
    if (this.state.type !== "removeDocument") {
      return;
    }
    const documentId = this.state.documentId;
    this.cleanupDocument(documentId);
    removeDocument(this.documents, documentId);
    this.save();
    this.dispatch(event.onSuccess);
  }

  get isSaving() {
    return !this.saveQueue.isEmpty || this.isSaveProjectScheduled;
  }

  save(
    event: SaveProjectEvent | AddDecisionsEvent | CopyDecisionsEvent | AddDocumentsEvent = {
      type: "dbot/save-project",
    },
  ) {
    logDebug("ProjectController::save", event, this.project.jsonData.generalMeeting);
    if (this.isLocked || this.isReadonly) {
      return;
    }
    this.clearValidationResult();
    this.dispatch({ type: "dbot/save-project-scheduled" });
    switch (event.type) {
      case "dbot/save-project":
        this.isSaveProjectScheduled = true;
        this.debouncedSaveProject();
        break;
      case "dbot/add-decisions":
        this.saveQueue.push((done) => this.doAddDecisions(event, done));
        break;
      case "dbot/copy-decisions":
        this.saveQueue.push((done) => this.doCopyDecisions(event, done));
        break;
      case "dbot/add-documents":
        this.saveQueue.push((done) => this.doAddDocuments(event, done));
        break;
    }
  }

  debouncedSaveProject = debounce(
    action(() => {
      this.isSaveProjectScheduled = false;
      this.saveQueue.push(this.doSaveProject);
    }),
    100,
  );

  handleSaveError(err: unknown) {
    if (isHttpError(HttpStatus.CONFLICT, err)) {
      this.dispatch([showWarning(() => <ConflictMessage dispatch={this.dispatch} />, {})]);
    } else {
      this.dispatch([showError({ key: "dbot.saveProjectError" })]);
    }
  }

  *doSaveProject(done: () => void) {
    logDebug("ProjectController::doSaveProject: starting");
    const jsonData: ProjectJsonData = {
      decisions: prepareDecisionsForSave(this.decisions),
      documents: prepareDocumentsForSave(this.documents),
      boardMeeting: this.boardMeetingFields.isChanged
        ? prepareBoardMeetingForSave(this.boardMeetingFields, this.project.jsonData)
        : this.project.jsonData.boardMeeting,
      generalMeeting: prepareGeneralMeetingForSave(
        this.generalMeetingFields,
        this.project.jsonData,
      ),
      roles: this.project.jsonData.roles,
      inputStates: [],
      data: this.data,
    };
    const payload: UpdateProjectRequest = {
      projectVersion: this.version,
      projectName: this.project.name,
      generatedName: this.nameGenerator?.(jsonData),
      jsonData: jsonData,
      batchDocumentOverrides: this.projectContext.isBatchProject
        ? prepareBatchDocumentOverrides(this.documents, this.project.batchCompanies)
        : undefined,
    };

    try {
      const res: AxiosResponse<GetProjectResponse> = yield this.http.put(
        `/project/${this.project.projectId}`,
        payload,
      );
      this.setProject(res.data);
      logDebug("ProjectController::doSaveProject: done!");

      this.dispatch({ type: "dbot/save-project-success" });
    } catch (err) {
      this.handleSaveError(err);
    } finally {
      this.boardMeetingFields.isChanged = false;
      done();
    }
  }

  *doAddDecisions(event: AddDecisionsEvent, done: () => void) {
    logDebug("ProjectController::doAddDecisions: starting");
    try {
      const req: AddDecisionsRequest = {
        projectVersion: this.version,
        decisions: event.decisions,
        index: event.options?.index,
      };
      const existingProject = this.project;
      const res: AxiosResponse<AddDecisionsResponse> = yield this.http.post(
        `/project/${this.projectId}/add-decisions`,
        req,
      );
      this.setProject(res.data.project);
      for (const decisionId of res.data.decisionIds) {
        const index = res.data.project.jsonData.decisions.findIndex(
          (d) => d.decisionId === decisionId,
        );
        const decision = this.initDecision(res.data.project.jsonData.decisions[index], index);
        if (decision.type === DecisionType.approveAnnualAccount) {
          this.generalMeetingFields.generalMeetingType.setValue(GeneralMeetingType.regular);
        }
        when(event.onSuccess, this.dispatch);
        this.dispatch({
          type: "dbot/decision-added",
          decisionId: decision.decisionId,
          isFreetext: decision.type === DecisionType.freetext,
        });
        if (res.data.decisionIds.length === 1 && decision.type !== DecisionType.freetext) {
          this.dispatch({
            type: "dbot/open-details-panel",
            panel: {
              type: "decision",
              decisionId: decision.decisionId,
              documentId: event.options?.documentId,
            },
          });
        }
      }

      const missingDocs = getMissingDocuments(existingProject, res.data.project);
      if (missingDocs.length) {
        this.dispatch(
          confirmAction({
            title: { key: "dbot.confirm.add-required-documents.title" },
            text: {
              key: "dbot.confirm.add-required-documents.text",
              params: { documents: { type: "md-list", value: missingDocs.map(getDocumentLabel) } },
            },
            confirmLabel: { key: "yes" },
            cancelLabel: { key: "no" },
            onConfirm: [
              thunk(() =>
                this.dispatch([{ type: "dbot/add-documents", documentTypes: missingDocs }]),
              ),
            ],
          }),
        );
      }

      logDebug("ProjectController::doAddDecision: done");
    } catch (err) {
      this.handleSaveError(err);
      when(event.onFailure, this.dispatch);
    } finally {
      done();
    }
  }

  *doCopyDecisions(event: CopyDecisionsEvent, done: () => void) {
    logDebug("ProjectController::doCopyDecisions: starting");
    try {
      const req: CopyDecisionsRequest = {
        projectVersion: this.version,
        projectId: event.projectId,
        decisions: event.decisions,
        index: event.options?.index,
      };
      const res: AxiosResponse<AddDecisionsResponse> = yield this.http.post(
        `/project/${this.projectId}/copy-decisions`,
        req,
      );
      this.setProject(res.data.project);
      for (const decisionId of res.data.decisionIds) {
        const index = res.data.project.jsonData.decisions.findIndex(
          (d) => d.decisionId === decisionId,
        );
        const decision = this.initDecision(res.data.project.jsonData.decisions[index], index);
        when(event.onSuccess, this.dispatch);
        this.dispatch({
          type: "dbot/decision-added",
          decisionId: decision.decisionId,
          isFreetext: decision.type === DecisionType.freetext,
        });
        if (res.data.decisionIds.length === 1 && decision.type !== DecisionType.freetext) {
          this.dispatch({
            type: "dbot/open-details-panel",
            panel: {
              type: "decision",
              decisionId: decision.decisionId,
              documentId: event.options?.documentId,
            },
          });
        }
      }
      logDebug("ProjectController::doCopyDecisions: done");
    } catch (err) {
      this.handleSaveError(err);
      when(event.onFailure, this.dispatch);
    } finally {
      done();
    }
  }

  *initEditDecision() {
    this.cleanupEditDecision();
    const detailsPanel = this.detailsPanel;
    if (detailsPanel?.type !== "decision") {
      return;
    }
    const orgNo = detailsPanel.orgNo ?? this.orgNo;
    const decisionId = detailsPanel.decisionId;
    const decision = this.decisions.find((d) => d.decisionId === decisionId);
    if (!decision) {
      return;
    }
    const decisionType = decision.type;

    if (orgNo === this.orgNo) {
      this.editedDecision = {
        decisionId,
        decisionType,
        orgNo,
        decision: ok(decision),
      };
    } else {
      this.editedDecision = {
        decisionId,
        decisionType,
        orgNo,
        decision: pending(),
      };
      try {
        const projectRes: AxiosResponse<GetProjectResponse> = yield this.http.get(
          `/project/${this.projectId}/company/${orgNo}`,
        );
        const shareholdersRes: AxiosResponse<GetShareholdersResponse> = yield this.http.get(
          `/shareholder/all/${orgNo}`,
        );
        const companyInfo: AxiosResponse<CompanyInfo> = yield this.http.get(`/company/${orgNo}`);
        const ctrl = new ProjectController(
          projectRes.data,
          {
            ...this.initializationParams,
            companyInfo: companyInfo.data,
            shareholderData: prepShareholderData(orgNo, shareholdersRes.data),
          },
          this.http,
          this.history,
          this.accessType,
        );
        const cleanup = ctrl.init();
        ctrl.addEventListener((event) => {
          switch (event.type) {
            case "dbot/save-project-success":
              this.dispatch({ type: "dbot/refresh-project-status" });
              break;
          }
        });
        const mappedDecisionId =
          this.projectContext.batchData[orgNo].decisionMapping[decisionId].targetDecisionId;
        this.editedDecision = {
          decisionId,
          decisionType,
          orgNo,
          decision:
            when(
              ctrl.decisions.find((d) => d.decisionId === mappedDecisionId),
              (d) => ok(d),
            ) ?? failure(),
          cleanup,
        };
      } catch (e) {
        this.editedDecision = undefined;
        this.dispatch([showGeneralError()]);
      }
    }
  }

  cleanupEditDecision() {
    if (this.editedDecision) {
      this.editedDecision.cleanup?.();
      this.editedDecision = undefined;
    }
  }

  cleanupEditDocument() {
    this.documents.map((d) => {
      if (d.form.type === DocumentType.general_meeting) {
        d.form.validateMessageError = [];
      }
    });
  }

  *doAddDocuments(
    { documentTypes, customDocumentType, expand, onSuccess, onFailure }: AddDocumentsEvent,
    done: () => void,
  ) {
    logDebug("ProjectController::doAddDocuments: starting");
    try {
      const req: AddDocumentsRequest = {
        documentTypes,
        customDocumentType: customDocumentType,
        projectVersion: this.version,
      };
      const res: AxiosResponse<AddDocumentsResponse> = yield this.http.post(
        `/project/${this.projectId}/add-documents`,
        req,
      );
      const existingDocuments = this.project.jsonData.documents;
      this.setProject(res.data.project);
      const addedDocuments = res.data.project.jsonData.documents.filter((d) =>
        res.data.documentIds.includes(d.documentId),
      );

      if (addedDocuments.length) {
        addedDocuments.forEach((d) => {
          if (
            d.documentData.type === "notice_general_meeting" ||
            d.documentData.type === "general_meeting"
          ) {
            this.generalMeetingFields = createGeneralMeetingFields({
              shareholders: this.projectContext.shareholderData.shareholders,
              roles: this.projectContext.companyInfo.roles,
              jsonData: res.data.project.jsonData,
              onChange: () => this.save(),
            });
          }
          const df = this.setupDocument(d, { customDocumentType });
          this.initDocument(df);
        });

        if (expand) {
          this.dispatch({
            type: "dbot/toggle-document-panel",
            documentId: addedDocuments[0].documentId,
            expanded: true,
          });
        }

        this.dispatch({
          type: "dbot/documents-added",
          addedDocumentTypes: addedDocuments.map((d) => d.documentData.type),
          existingDocumentTypes: existingDocuments.map((d) => d.documentData.type),
        });
      }
      logDebug("ProjectController::doAddDocuments: done");
      when(onSuccess, (f) =>
        this.dispatch(f({ documentIds: addedDocuments.map((d) => d.documentId) })),
      );
    } catch (err) {
      this.handleSaveError(err);
      when(onFailure, this.dispatch);
    } finally {
      done();
    }
  }

  isDocumentPanelExpanded(documentId: string) {
    return this.selectedDocumentId === documentId;
  }

  addEventListener(listener: (event: DbotEvent) => void): () => void {
    this.eventListeners.push(listener);
    return () => {
      this.removeEventListener(listener);
    };
  }

  removeEventListener(listener: (event: DbotEvent) => void) {
    this.eventListeners.splice(this.eventListeners.indexOf(listener, 1));
  }

  readonly decisionsForValidation: DecisionType[] = [
    DecisionType.assignsNewCompanyBoard,
    DecisionType.approveAnnualAccount,
    DecisionType.approveCompanyBoardRemuneration,
    DecisionType.approveNominationCommitteeRemuneration,
    DecisionType.electNominationCommittee,
    DecisionType.auditor,
    DecisionType.capitalIncreaseThroughBonusIssue,
    DecisionType.companyName,
    DecisionType.convertibleLoanPowerOfAttorney,
    DecisionType.dividend,
    DecisionType.dividendPowerOfAttorney,
    DecisionType.groupContributionByGeneralMeeting,
    DecisionType.increaseOfShareCapital,
    DecisionType.increaseShareCapitalPowerOfAttorney,
    DecisionType.acquireOwnSharesPowerOfAttorney,
    DecisionType.reductionOfShareCapital,
    DecisionType.changeArticlesOfAssociation,
    DecisionType.conversionOfShares,
    DecisionType.electionOfCompanyBoard,
  ];

  private validateProject({ onSuccess }: { onSuccess: DbotEvent }) {
    this.invalidDecisions = new Set(
      this.decisions
        .filter(
          (decision) =>
            this.decisionsForValidation.includes(decision.type) &&
            !getHandler(decision.type).validate(decision.form),
        )
        .map((decision) => decision.decisionId),
    );

    if (isEmpty(this.invalidDecisions)) {
      this.dispatch(onSuccess);
    }
  }

  clearValidationResult() {
    this.invalidDecisions = new Set();
  }
}

type UpdateProjectRequest = {
  projectVersion: number;
  projectName?: string;
  generatedName?: string;
  jsonData: ProjectJsonData;
  batchDocumentOverrides?: BatchDocumentOverrrides[];
};

type AddDecisionsRequest = {
  projectVersion: number;
  decisions: AddDecisionItem[];
  index?: number;
};

type CopyDecisionsRequest = {
  projectVersion: number;
  projectId: string;
  decisions: string[];
  index?: number;
};

export type AddDecisionItem = {
  decisionType: DecisionType;
  title?: string;
};

type AddDecisionsResponse = {
  decisionIds: string[];
  project: GetProjectResponse;
};

type AddDocumentsRequest = {
  projectVersion: number;
  documentTypes: DocumentType[];
  customDocumentType?: CustomDocumentType;
};

type AddDocumentsResponse = {
  documentIds: string[];
  project: GetProjectResponse;
};

function forwardEvent(
  event: DbotEvent,
  decisions: DecisionAndForm<DecisionType>[],
  documents: DocumentAndForm[],
  summaryController: SummaryController,
  electronicGeneralMeetingController: AdminElectronicGeneralMeetingController,
  eventListeners: Array<(event: DbotEvent) => void>,
) {
  for (const d of decisions) {
    const h = getHandler(d.type);
    if (h.handleEvent) {
      h.handleEvent(d.form, event);
    }
  }
  for (const d of documents) {
    const h = getDocumentHandler(d.type);
    if (h.handleEvent) {
      h.handleEvent(d.form, event);
    }
  }
  summaryController.handleEvent(event);
  electronicGeneralMeetingController.handleEvent(event);
  for (const l of eventListeners) {
    l(event);
  }
}

function toDocumentItem(d: DocumentAndForm): DocumentItem {
  return {
    documentId: d.documentId,
    type: d.type,
    title: getDocumentLabel(d.type),
    languages: mapDocumentLanguageToLocales(d.form.documentLanguage.value),
  };
}

function toDecisionItem(
  d: DecisionAndForm<DecisionType>,
  hasValidationError: boolean,
  includeInGeneralMeeting: boolean,
  defaultGeneralMeetingVotingType: GeneralMeetingVotingType,
  isApprovedByGeneralMeeting: boolean,
): DecisionItem {
  return {
    decisionId: d.decisionId,
    title: getDecisionActionLabel(d.type, d.decision.decisionData),
    type: d.type,
    decisionTexts: d.decisionTexts ?? [],
    attachments: d.attachments,
    decisionData: d.decision?.decisionData,
    includeInGeneralMeeting,
    defaultGeneralMeetingVotingType,
    isApprovedByGeneralMeeting,
    hasValidationError,
  };
}

export function prepareDocumentPreview(preview?: DocumentPreview): string | undefined {
  return preview?.content;
}

export function addCompanyRole(companyInfo: CompanyInfo, role: CompanyRole) {
  if (!companyInfo.roles.find((r) => r.companyroleId === role.companyroleId)) {
    companyInfo.roles.push(role);
  }
}

function replaceLocalizedTextForDecision(
  data: LocalizedTextForDocument,
  decision: DecisionAndForm<DecisionType>,
) {
  const index = decision.decisionTexts.findIndex(
    (t) => t.documentType === data.documentType && t.language === data.language,
  );
  if (index > -1) {
    decision.decisionTexts[index] = data;
  } else {
    decision.decisionTexts.push(data);
  }
}

export function getUpdatedRoles(
  roles: CompanyRole[],
  updates: UpdateBoardMeetingRecipientItem[],
): UpdateCompanyRoleItem[] {
  const optionsById = new Map<string | number, CompanyRole>(roles.map((o) => [o.companyroleId, o]));
  return updates.flatMap((u) => {
    const option = optionsById.get(u.id);
    if (option?.companyroleId) {
      return [{ companyroleId: option.companyroleId, email: u.email }];
    }
    return [];
  });
}

function isDecisionLocked(
  { projectContext, editedDecision, detailsPanel }: ProjectController,
  decisionId: string,
): boolean {
  const documentId = detailsPanel?.type === "decision" ? detailsPanel.documentId : undefined;
  return (
    projectContext.isLocked ||
    (editedDecision !== undefined &&
      editedDecision.decisionId === decisionId &&
      documentId !== undefined &&
      isDocumentLocked(projectContext, documentId))
  );
}

export function createDecisionContext(
  decisionId: string,
  decisionTexts: LocalizedTextForDocument[],
  attachments: ProjectDecisionAttachment[],
  controller: ProjectController,
): DecisionContext {
  const projectContext = controller.projectContext;
  return makeAutoObservable({
    decisionId,
    decisionTexts,
    get inputState() {
      return findStateForDecision(projectContext.inputStates ?? [], decisionId);
    },
    get projectRoles() {
      return projectContext.projectRoles;
    },
    get boardMeetingFields() {
      return projectContext.boardMeetingFields;
    },

    get boardMeetingNoticeRecipients() {
      return projectContext.boardMeetingNoticeRecipients;
    },

    get boardMeetingMinutesParticipants() {
      return projectContext.boardMeetingMinutesParticipants;
    },

    get companyInfo() {
      return projectContext.companyInfo;
    },

    get currentDecisions() {
      return projectContext.currentDecisions;
    },

    get currentDocuments() {
      return projectContext.currentDocuments;
    },

    get currentSubscription() {
      return projectContext.currentSubscription;
    },

    get documentLanguages() {
      return projectContext.documentLanguages;
    },

    get documentStatuses() {
      return projectContext.documentStatuses;
    },

    get generalMeetingFields() {
      return projectContext.generalMeetingFields;
    },

    get isLocked() {
      return isDecisionLocked(controller, decisionId);
    },

    get orgNo() {
      return projectContext.orgNo;
    },

    get projectId() {
      return projectContext.projectId;
    },

    get shareholderData() {
      return projectContext.shareholderData;
    },

    get signingBoardMembers() {
      return projectContext.signingBoardMembers;
    },

    get subscriptionTypeModel() {
      return projectContext.subscriptionTypeModel;
    },

    get fileUploadConfig() {
      return projectContext.fileUploadConfig;
    },

    get user() {
      return projectContext.user;
    },

    get inputStates(): InputState[] {
      return projectContext.inputStates;
    },

    get aiClient(): AIClient {
      return projectContext.aiClient;
    },

    get projectStatus(): ProjectStatus {
      return projectContext.projectStatus;
    },

    get isSharepointEnabled(): boolean {
      return projectContext.isSharepointEnabled;
    },

    get locale() {
      return projectContext.locale;
    },

    get detailsPanelMaximized() {
      return projectContext.detailsPanelMaximized;
    },

    get boardMeetingVotes() {
      return projectContext.boardMeetingVotes;
    },

    get displayMode() {
      return projectContext.displayMode;
    },

    get participationList() {
      return projectContext.participationList;
    },

    get powerOfAttorneys() {
      return projectContext.powerOfAttorneys;
    },

    get companies() {
      return projectContext.companies;
    },

    get batchData() {
      return projectContext.batchData;
    },

    get companyContexts() {
      return projectContext.companyContexts;
    },

    get isBatchProject() {
      return projectContext.isBatchProject;
    },

    get documentSigners() {
      return projectContext.documentSigners;
    },

    get userPrefs() {
      return projectContext.userPrefs;
    },

    get state() {
      return projectContext.state;
    },

    get jsonData() {
      return projectContext.jsonData;
    },

    get hasSavedDefaultDocumentLanguage() {
      return projectContext.hasSavedDefaultDocumentLanguage;
    },

    get isProd() {
      return projectContext.isProd;
    },

    get featureToggles() {
      return projectContext.featureToggles;
    },

    get subscribers() {
      return projectContext.subscribers;
    },

    get attachments() {
      return (
        controller.decisions.find((d) => d.decisionId === decisionId)?.attachments ?? attachments
      );
    },

    getData(req) {
      return projectContext.getData(req);
    },

    addData(data: DataRequest) {
      projectContext.addData(data);
    },
  });
}

function createDocumentContext(
  documentId: string,
  ctrl: ProjectController,
): DocumentContext<DocumentData> {
  return {
    documentId,

    get documentData() {
      return ctrl.project.jsonData.documents.find((d) => d.documentId === documentId)!.documentData;
    },

    get documentOverrides() {
      return Object.fromEntries(
        ctrl.project.batchDocumentOverrides
          .filter((x) => x.documentId === documentId)
          .map((x) => [x.orgNo, x.overrides]),
      );
    },

    get projectRoles() {
      return ctrl.projectContext.projectRoles;
    },

    get boardMeetingFields() {
      return ctrl.projectContext.boardMeetingFields;
    },

    get boardMeetingNoticeRecipients() {
      return ctrl.projectContext.boardMeetingNoticeRecipients;
    },

    get boardMeetingMinutesParticipants() {
      return ctrl.projectContext.boardMeetingMinutesParticipants;
    },

    get companyInfo() {
      return ctrl.projectContext.companyInfo;
    },

    get currentDecisions() {
      return ctrl.projectContext.currentDecisions;
    },

    get currentDocuments() {
      return ctrl.projectContext.currentDocuments;
    },

    get currentSubscription() {
      return ctrl.projectContext.currentSubscription;
    },

    get documentLanguages() {
      return ctrl.projectContext.documentLanguages;
    },

    get documentStatuses() {
      return ctrl.projectContext.documentStatuses;
    },

    get generalMeetingFields() {
      return ctrl.projectContext.generalMeetingFields;
    },

    get isProjectLocked() {
      return ctrl.projectContext.isLocked;
    },

    get isLocked() {
      return isDocumentLocked(ctrl.projectContext, documentId);
    },

    get orgNo() {
      return ctrl.projectContext.orgNo;
    },

    get projectId() {
      return ctrl.projectContext.projectId;
    },

    get shareholderData() {
      return ctrl.projectContext.shareholderData;
    },

    get signingBoardMembers() {
      return ctrl.projectContext.signingBoardMembers;
    },

    get subscriptionTypeModel() {
      return ctrl.projectContext.subscriptionTypeModel;
    },

    get fileUploadConfig() {
      return ctrl.projectContext.fileUploadConfig;
    },

    get user() {
      return ctrl.projectContext.user;
    },

    get inputStates(): InputState[] {
      return ctrl.projectContext.inputStates;
    },

    get aiClient(): AIClient {
      return ctrl.projectContext.aiClient;
    },

    get projectStatus(): ProjectStatus {
      return ctrl.projectContext.projectStatus;
    },

    get isSharepointEnabled(): boolean {
      return ctrl.projectContext.isSharepointEnabled;
    },

    get locale() {
      return ctrl.projectContext.locale;
    },

    get detailsPanelMaximized() {
      return ctrl.projectContext.detailsPanelMaximized;
    },

    get boardMeetingVotes() {
      return ctrl.projectContext.boardMeetingVotes;
    },

    get displayMode() {
      return ctrl.projectContext.displayMode;
    },

    get participationList() {
      return ctrl.projectContext.participationList;
    },

    get powerOfAttorneys() {
      return ctrl.projectContext.powerOfAttorneys;
    },

    get companies() {
      return ctrl.projectContext.companies;
    },

    get batchData() {
      return ctrl.projectContext.batchData;
    },

    get companyContexts() {
      return ctrl.projectContext.companyContexts;
    },

    get isBatchProject() {
      return ctrl.projectContext.isBatchProject;
    },

    get documentSigners() {
      return ctrl.projectContext.documentSigners;
    },

    get userPrefs() {
      return ctrl.projectContext.userPrefs;
    },

    get state() {
      return ctrl.projectContext.state;
    },

    get jsonData() {
      return ctrl.projectContext.jsonData;
    },

    get hasSavedDefaultDocumentLanguage() {
      return ctrl.projectContext.hasSavedDefaultDocumentLanguage;
    },

    get isProd() {
      return ctrl.projectContext.isProd;
    },

    get featureToggles() {
      return ctrl.projectContext.featureToggles;
    },

    get subscribers() {
      return ctrl.projectContext.subscribers;
    },

    get attachments() {
      return ctrl.documents.find((d) => d.documentId === documentId)!.attachments;
    },

    getData(req) {
      return ctrl.projectContext.getData(req);
    },

    addData(d: DataRequest) {
      ctrl.projectContext.addData(d);
    },
  };
}

export const getProjectMenuItems = (
  summaryController: SummaryController,
  dispatch: DbotEventDispatcher,
  projectName: string,
  t: TFunction,
): DropdownMenuItems =>
  compact([
    { type: "label", label: t("project") },
    {
      type: "item",
      label: t("duplicate"),
      icon: "copy",
      onSelect: () =>
        dispatch({
          type: "dbot/duplicate-project",
          name: t("dbot.duplicate.projectName", { name: projectName }),
        }),
    },
    {
      type: "item",
      label: t("delete"),
      icon: "trash",
      color: "error",
      onSelect: () => dispatch({ type: "dbot/delete-project" }),
      isDisabled: anyDocumentSentForSigning(summaryController.context.documentStatuses),
      tooltip: anyDocumentSentForSigning(summaryController.context.documentStatuses)
        ? t("dbot.projectList.deleteDisabled")
        : undefined,
    },
    { type: "separator" },
    {
      type: "item",
      icon: "chat-dots",
      label: t("give-us-feedback"),
      onSelect: () => {
        openLinkInNewWindow("https://airtable.com/apppdnKJrnENisz3z/shrKU5vSljC5QZs3j");
      },
    },
  ]);

export const documentStatusTag = (
  context: { documentStatuses: ProjectDocumentStatus[] },
  documentId: string,
): TagProps =>
  isDocumentLocked(context, documentId)
    ? {
        icon: "lock",
        label: { key: "dbot.locked" },
        color: "neutral",
        tooltip: { key: "dbot.documentLocked" },
      }
    : {
        icon: "pen",
        label: { key: "draft" },
        color: "neutral",
      };

export const getTabLabel = (tab: TabId): TMessage => {
  switch (tab) {
    case "companies":
      return { key: "dbot.companies" };
    case "documents":
      return { key: "dbot.documents" };
    case "follow-up":
      return { key: "dbot.follow-up" };
    case "matters":
      return { key: "dbot.matters" };
    case "meetings":
      return { key: "header.meetings" };
  }
};

export function getVisibleTabs({
  decisions,
  documents,
  locale,
  conductElectronicGeneralMeeting,
  displayMode,
  isBatchProject,
}: {
  decisions: DecisionListItem[];
  documents: DocumentAndForm[];
  locale: Language;
  conductElectronicGeneralMeeting?: boolean;
  displayMode?: "fullscreen" | "screen-share" | "normal" | "overlay";
  isBatchProject?: boolean;
}): TabId[] {
  if (displayMode === "screen-share") {
    return [];
  }
  return compact([
    isBatchProject && "companies",
    (documents.length !== 1 || decisions.some((d) => d.decision.type !== DecisionType.freetext)) &&
      "matters",
    "documents",
    conductElectronicGeneralMeeting && "meetings",
    hasFollowUpDecisions(decisions, locale) && "follow-up",
  ]);
}

export const documentSendingOrSigningStatusTag = (
  context: { documentStatuses: ProjectDocumentStatus[] },
  documentId: string,
): TagProps | undefined =>
  isDocumentSigned(context, documentId)
    ? {
        icon: "signature",
        label: { key: "dbot.summary.signingStatus.signed" },
        color: "success",
      }
    : isDocumentSentForSigning(context, documentId)
      ? {
          icon: "pen",
          label: { key: "sent-for-signing" },
          color: "success",
        }
      : isDocumentSentForReview(context, documentId)
        ? { icon: "paper-plane", label: { key: "sent-for-review" }, color: "info" }
        : isDocumentSentForVoting(context, documentId)
          ? { icon: "paper-plane", label: { key: "sent-for-voting" }, color: "info" }
          : isDocumentSent(context, documentId)
            ? { icon: "paper-plane", label: { key: "sent" }, color: "success" }
            : undefined;

function hasFollowUpDecisions(decisions: DecisionListItem[], locale: Language): boolean {
  return decisions.some((d) => getDecisionFollowUpPanels(d, locale).length > 0);
}

export const reviewDocumentId = "review-matters-id";
export const conductGeneralMeetingDocumentId = "conduct-general-meeting-id";

export function findBoardMeetingMinutes(jsonData: ProjectJsonData) {
  return jsonData.documents.find((f) => f.documentData.type === DocumentType.board_meeting)
    ?.documentId;
}

export const isDecisionExcludedInDocument = (
  decisionItem: DecisionItem,
  documentType: DocumentType,
): boolean => {
  return (
    decisionItem.decisionData?.type === DecisionType.freetext &&
    !showFreetext(decisionItem.decisionData, documentType)
  );
};

export const getMissingDocuments = (
  existingProject: GetProjectResponse,
  updatedProject: GetProjectResponse,
): DocumentType[] => {
  const gmTypes = [DocumentType.notice_general_meeting, DocumentType.general_meeting];
  const requiresGm =
    updatedProject.decisionsRequiresGeneralMeeting.length >
    existingProject.decisionsRequiresGeneralMeeting.length;
  const requiresAoa =
    updatedProject.decisionsRequiringArticlesOfAssociation.length >
    existingProject.decisionsRequiringArticlesOfAssociation.length;
  const containsGm = updatedProject.jsonData.documents.some((d) =>
    gmTypes.includes(d.documentData.type),
  );
  const containsAoa = updatedProject.jsonData.documents.some(
    (d) => d.documentData.type === DocumentType.articlesofassociation,
  );

  return [
    ...(requiresGm && !containsGm ? gmTypes : []),
    ...(requiresAoa && !containsAoa ? [DocumentType.articlesofassociation] : []),
  ];
};

export const getProjectDocumentTitle = (d: DocumentAndForm, lang: Language): TMessage =>
  getDocumentHandler(d.type)?.getTitle?.(d.form, lang) ?? getDocumentLabel(d.type);
